import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import DesignServicesOutlinedIcon from "@mui/icons-material/DesignServicesOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CloseIcon from "@mui/icons-material/Close";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import AcUnitOutlinedIcon from "@mui/icons-material/AcUnitOutlined";
import DeviceThermostatOutlinedIcon from "@mui/icons-material/DeviceThermostatOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import jsPDF from "jspdf";
import { MainRoutes } from "common/config/route.config";
import { ToastMessageUtil } from "common/components/base/toast-message/ToastMessage.util";
import { ToastVariant } from "common/enums/controls/toast.enum";
import { useLazyGetAdminDocumentsQuery } from "modules/documents/data/api/queries/listAdminDocuments";
import {
  useCreateMocMutation,
  useUpdateMocMutation,
  useUpdateAssessmentStatusMutation,
  useGenerateFinalMocReportMutation,
  useUpdateDecisionSummaryMutation
} from "../../data/api/commands/mocCommands";
import {
  useLazyGetMocByIdQuery,
  useLazyGetMocEngineeringAnalysisQuery,
  useLazyGetMocImpactAssessmentQuery,
  useLazyGetMocRiskAssessmentQuery,
  useLazyGetMocActionRegisterQuery,
  useLazyGetMocSafeguardsQuery,
  useLazyGetMocDecisionPackageQuery
} from "../../data/api/queries/getMocs";
import {
  MocAction,
  MocRequest,
  AssessmentType,
  DecisionPackageView
} from "../../data/models/moc.model";
import { formattedDate } from "common/utils/date";
import GraphCanvas from "modules/knowledge-library/pages/knowledge-library/components/graph-canvas/GraphCanvas";
import "./CreateMoc.scss";

const STEPS = [
  "Initiate Change",
  "Engineering Analysis",
  "Assessment Workspace",
  "Decision Package & Review"
];

const ASSET_OPTIONS = [
  "FPSO",
  "Platform A-12",
  "Refinery Unit 4",
  "Terminal South",
  "Field Ops 2"
];

const TRIGGER_OPTIONS = [
  "Reliability Issue",
  "Safety Concern",
  "Regulatory Compliance",
  "Efficiency Improvement",
  "Obsolescence"
];

// Standard currency symbols so the financial impact amount is captured with a
// consistent currency prefix (stored as a single string for the backend).
const CURRENCY_OPTIONS = ["$"];

// Standard schedule units so the schedule impact is captured consistently
// (stored as a single string such as "2 Weeks" for the backend).
const SCHEDULE_UNIT_OPTIONS = ["Hours", "Days", "Weeks"];

const AI_GUIDANCE = [
  "Describe current conditions vs. proposed state.",
  "Explicitly list hardware/software modifications.",
  "Define expected operational benefits.",
  "State technical assumptions or constraints."
];

const ANALYSIS_METRICS = [
  { label: "Engineering Confidence", value: "94%", highlight: true },
  { label: "Affected Systems", value: "06" },
  { label: "Potential High -Risk Findings", value: "03" },
  { label: "Primary Recommendation", value: "03" }
];

interface MocOption {
  id: string;
  title: string;
  description: string;
}

const MOC_OPTIONS: MocOption[] = [
  {
    id: "formal",
    title: "Formal MOC Required",
    description: "Operating envelope, safeguards and procedures may change."
  },
  {
    id: "not-required",
    title: "MOC Not Required",
    description: "Not appropriate for this case based on current evidence."
  },
  {
    id: "more-info",
    title: "More Information Required",
    description: "Applicable only if key engineering context is unavailable."
  }
];

type AssetTab = "diagram" | "documents";

interface UploadSection {
  key: AssetTab;
  title: string;
  description: string;
  buttonLabel: string;
}

const UPLOAD_SECTIONS: UploadSection[] = [
  {
    key: "diagram",
    title: "Engineering Diagram",
    description:
      "Attach the primary P&ID , PFD, or isometric drawing that depicts the affected system for AI geometric analysis.",
    buttonLabel: "Select Drawing"
  },
  {
    key: "documents",
    title: "Engineering Documents",
    description:
      "Upload datasheets, calculation notes, or risk assessments to provide further context for the analysis.",
    buttonLabel: "Select Document"
  }
];

interface AssetDoc {
  id: string;
  name: string;
  date: string;
  source: string;
  size: string;
  status?: string;
}

const ASSET_LIBRARY: Record<AssetTab, AssetDoc[]> = {
  diagram: [
    {
      id: "dwg-1",
      name: "B-304-P&ID- Process- Unit -01.dwg",
      date: "Apr 7, 2026",
      source: "Processing Hub",
      size: "24.5 mb"
    },
    {
      id: "dwg-2",
      name: "B-308-P&ID- Process- Unit -02.dwg",
      date: "Apr 7, 2026",
      source: "Processing Hub",
      size: "28.5 mb"
    },
    {
      id: "dwg-3",
      name: "FPSO-Sample Spec",
      date: "Apr 7, 2026",
      source: "Processing Hub",
      size: "12.0 mb"
    },
    {
      id: "dwg-4",
      name: "PVM-SU-4750",
      date: "Apr 7, 2026",
      source: "Processing Hub",
      size: "8.3 mb"
    }
  ],
  documents: [
    {
      id: "doc-1",
      name: "B-304-P&ID- Process- Unit -01.doc",
      date: "Apr 7, 2026",
      source: "Processing Hub",
      size: "24.5 mb"
    },
    {
      id: "doc-2",
      name: "B-308-P&ID- Process- Unit -02.doc",
      date: "Apr 7, 2026",
      source: "Processing Hub",
      size: "28.5 mb"
    },
    {
      id: "doc-3",
      name: "FPSO-Sample Spec",
      date: "Apr 7, 2026",
      source: "Processing Hub",
      size: "12.0 mb"
    },
    {
      id: "doc-4",
      name: "Compressor_Requirment.pdf",
      date: "Apr 7, 2026",
      source: "Processing Hub",
      size: "5.1 mb"
    },
    {
      id: "doc-5",
      name: "API_610_centrifugal",
      date: "Apr 7, 2026",
      source: "Processing Hub",
      size: "3.4 mb"
    }
  ]
};

interface InitiateChangeForm {
  mocTitle: string;
  asset: string;
  equipment: string;
  trigger: string;
  proposedChange: string;
  scheduleImpact: string;
  financialImpact: string;
  businessJustification: string;
}

const INITIAL_FORM: InitiateChangeForm = {
  mocTitle: "",
  asset: "",
  equipment: "",
  trigger: TRIGGER_OPTIONS[0],
  proposedChange: "",
  scheduleImpact: "",
  financialImpact: "",
  businessJustification: ""
};

type AssessmentRating = "HIGH" | "MEDIUM" | "LOW";

interface AssessmentKpi {
  id: string;
  label: string;
  value: string;
  unit: string;
  caption: string;
  accent: "red" | "orange" | "dark" | "green";
}

interface AssessmentRow {
  id: string;
  dimension: string;
  assessment: string;
  rationale: string;
  evidence: string[];
  rating: AssessmentRating;
}

const ASSESSMENT_KPIS: AssessmentKpi[] = [
  {
    id: "impact",
    label: "Overall Impact",
    value: "High",
    unit: "Affected",
    caption: "Operating envelope affected",
    accent: "red"
  },
  {
    id: "risk",
    label: "Residual Risk",
    value: "Medium",
    unit: "Level",
    caption: "After safeguards",
    accent: "orange"
  },
  {
    id: "systems",
    label: "Affected Systems",
    value: "6",
    unit: "Items",
    caption: "Documents and Assets",
    accent: "dark"
  },
  {
    id: "confidence",
    label: "Engineering Confidence",
    value: "94%",
    unit: "Score",
    caption: "Validated references",
    accent: "green"
  }
];

const ASSESSMENT_TABS = [
  "Impact Assessment",
  "Risk Assessment",
  "Action Register",
  "Safeguards"
];

const formatImpactCategory = (category: string): string =>
  category
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const normalizeImpactRating = (severity: string): AssessmentRating => {
  const normalized = (severity || "").toUpperCase();
  if (normalized === "HIGH" || normalized === "LOW") {
    return normalized;
  }
  return "MEDIUM";
};

interface RiskRow {
  id: string;
  statement: string;
  consequence: string;
  safeguards: string[];
  mitigation: string;
  severity: number;
  likelihood: number;
  initial: AssessmentRating;
  residual: AssessmentRating;
}

interface ActionRow {
  id: string;
  code: string;
  action: string;
  owner: string;
  dueStage: string;
  priority: "HIGH" | "MEDIUM" | "LOW";
  status: "Accepted" | "Rejected";
  highlighted?: boolean;
}

interface SafeguardRow {
  id: string;
  hazard: string;
  safeguard: string;
  criticality: AssessmentRating;
  owner: string;
}

interface DecisionKpi {
  id: string;
  label: string;
  value: string;
  accent: "green" | "dark" | "orange" | "muted";
}

const VALIDATION_CHECKLIST = [
  "Engineering Analysis",
  "Impact Assessment",
  "Risk Assessment",
  "Action Register",
  "Executive Summary",
  "Audit Trail Finalized"
];

interface DecisionView {
  kpis: DecisionKpi[];
  recommendation: {
    title: string;
    chips: { label: string; value: string }[];
    text: string;
  };
  executiveSummary: { label: string; text: string }[];
  sources: { documents: number; historical: number; standards: string };
  generatedOn: string;
  impact: {
    exposure: string;
    accepted: number;
    rejected: number;
    pending: number;
  };
  risk: {
    residual: string;
    highRisks: number;
    mitigations: number;
    mitigationPct: number;
    accepted: number;
    rejected: number;
    pending: number;
  };
  safeguards: { score: number; items: { name: string; status: string }[] };
  actions: {
    total: number;
    completed: number;
    pending: number;
    overdue: number;
  };
}

const DEFAULT_DECISION_VIEW: DecisionView = {
  kpis: [
    { id: "required", label: "MOC Required", value: "-", accent: "green" },
    { id: "readiness", label: "Readiness", value: "-", accent: "dark" },
    { id: "risk", label: "Residual Risk", value: "-", accent: "orange" },
    { id: "approval", label: "Approval Status", value: "-", accent: "muted" }
  ],
  recommendation: { title: "", chips: [], text: "" },
  executiveSummary: [],
  sources: { documents: 0, historical: 0, standards: "" },
  generatedOn: "",
  impact: { exposure: "", accepted: 0, rejected: 0, pending: 0 },
  risk: {
    residual: "",
    highRisks: 0,
    mitigations: 0,
    mitigationPct: 0,
    accepted: 0,
    rejected: 0,
    pending: 0
  },
  safeguards: { score: 0, items: [] },
  actions: { total: 0, completed: 0, pending: 0, overdue: 0 }
};

const titleCase = (value?: string): string =>
  value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : "";

const formatGeneratedOn = (iso?: string): string => {
  if (!iso) {
    return "";
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

// Maps the decision package API response to the shape the UI/PDF consume,
// falling back to defaults until the endpoint is available.
const buildDecisionView = (data?: DecisionPackageView | null): DecisionView => {
  if (!data) {
    return DEFAULT_DECISION_VIEW;
  }
  const { recommendation, executiveSummary, engineeringSources } = data;
  const { impactAssessment, riskAssessment, engineeringActions } = data;
  const safeguards = data.safeguards;
  const actionsTotal = engineeringActions?.total ?? 0;
  const actionsAccepted = engineeringActions?.accepted ?? 0;
  const riskTotal = riskAssessment?.total ?? 0;
  const riskAccepted = riskAssessment?.accepted ?? 0;
  return {
    kpis: [
      {
        id: "required",
        label: "MOC Required",
        value: recommendation?.mocRequired ? "YES" : "NO",
        accent: "green"
      },
      {
        id: "readiness",
        label: "Confidence Score",
        value:
          recommendation?.engineeringConfidence != null
            ? `${Math.round(recommendation.engineeringConfidence)}%`
            : "-",
        accent: "dark"
      },
      {
        id: "risk",
        label: "Residual Risk",
        value: titleCase(riskAssessment?.residualRisk),
        accent: "orange"
      },
      {
        id: "approval",
        label: "Approval Status",
        value: data.is_report_generated ? "Report Generated" : "In Draft",
        accent: "muted"
      }
    ],
    recommendation: {
      title: recommendation?.title ?? "",
      chips: [
        {
          label: "AI Confidence",
          value: `${recommendation?.engineeringConfidence ?? 0}%`
        },
        {
          label: "Engineering Impact",
          value: titleCase(recommendation?.engineeringImpact)
        },
        {
          label: "Residual Risk",
          value: titleCase(recommendation?.residualRisk)
        },
        {
          label: "Compliance",
          value: `${recommendation?.complianceScore ?? 0}% Verified`
        },
        {
          label: "Actions",
          value: `${recommendation?.actionsGenerated ?? 0} Generated`
        }
      ],
      text: recommendation?.recommendationText ?? ""
    },
    executiveSummary: [
      {
        label: "BUSINESS OBJECTIVE",
        text: executiveSummary?.businessObjective ?? ""
      },
      {
        label: "ENGINEERING IMPACT",
        text: executiveSummary?.engineeringImpact ?? ""
      },
      { label: "RESIDUAL RISK", text: executiveSummary?.residualRisk ?? "" },
      {
        label: "COMPLIANCE OUTCOME",
        text: executiveSummary?.complianceOutcome ?? ""
      },
      {
        label: "IMPLEMENTATION READINESS",
        text: executiveSummary?.implementationReadiness ?? ""
      }
    ],
    sources: {
      documents: engineeringSources?.documentsReviewed ?? 0,
      historical: engineeringSources?.historicalMocsReferenced ?? 0,
      standards: (engineeringSources?.standardsReviewed || []).join(", ")
    },
    generatedOn: engineeringSources?.generatedOn
      ? `Generated on ${formatGeneratedOn(engineeringSources.generatedOn)}`
      : "",
    impact: {
      exposure: `${titleCase(impactAssessment?.exposure)} Exposure`,
      accepted: impactAssessment?.accepted ?? 0,
      rejected: impactAssessment?.rejected ?? 0,
      pending: impactAssessment?.pending ?? 0
    },
    risk: {
      residual: `Residual: ${titleCase(riskAssessment?.residualRisk)}`,
      highRisks: riskAssessment?.highRisksIdentified ?? 0,
      mitigations: riskAssessment?.mitigationsApplied ?? 0,
      mitigationPct: riskTotal
        ? Math.round((riskAccepted / riskTotal) * 100)
        : 0,
      accepted: riskAssessment?.accepted ?? 0,
      rejected: riskAssessment?.rejected ?? 0,
      pending: riskAssessment?.pending ?? 0
    },
    safeguards: {
      score: safeguards?.complianceScore ?? 0,
      items: safeguards?.items || []
    },
    actions: {
      total: actionsTotal,
      completed: actionsAccepted,
      pending: engineeringActions?.pending ?? 0,
      overdue: engineeringActions?.rejected ?? 0
    }
  };
};

const CreateMoc: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const locationState = location.state as {
    moc?: { mocId?: string; title?: string; asset?: string };
    mode?: "view" | "edit";
    initialStep?: number;
  } | null;
  const editMoc = locationState?.moc;
  const isViewMode = locationState?.mode === "view";

  const [activeStep, setActiveStep] = useState(locationState?.initialStep ?? 0);
  const [form, setForm] = useState<InitiateChangeForm>(
    editMoc
      ? {
          ...INITIAL_FORM,
          mocTitle: editMoc.title ?? "",
          asset: editMoc.asset ?? ""
        }
      : INITIAL_FORM
  );
  const [expandedSystem, setExpandedSystem] = useState<string | null>(null);
  const [selectedGraphNodeId, setSelectedGraphNodeId] = useState<string | null>(
    null
  );
  const [selectedMocOption, setSelectedMocOption] = useState<string>(
    MOC_OPTIONS[0].id
  );
  const [activeAssessmentTab, setActiveAssessmentTab] = useState<string>(
    ASSESSMENT_TABS[0]
  );
  const [assessmentDecisions, setAssessmentDecisions] = useState<
    Record<string, "accept" | "reject">
  >({});
  const [selectedAssets, setSelectedAssets] = useState<
    Record<AssetTab, AssetDoc[]>
  >({
    diagram: [],
    documents: []
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTab, setModalTab] = useState<AssetTab>("diagram");
  const [draftSelection, setDraftSelection] = useState<
    Record<AssetTab, string[]>
  >({
    diagram: [],
    documents: []
  });
  // Blocks navigation to Engineering Analysis while the backend runs the async
  // analysis; cleared once currentStage reaches ANALYSIS_COMPLETED.
  const [isAnalysing, setIsAnalysing] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  // Shown after a successful Analyse submit so the user can wait or jump back
  // to the MOC list while the backend runs the async analysis.
  const [showAnalysisPopup, setShowAnalysisPopup] = useState(false);
  // True once the submit POST has completed, switching the popup to its
  // options state (Continue / Back to MOC List).
  const [analysisSubmitted, setAnalysisSubmitted] = useState(false);
  // Set when the user picks "Wait here" in the post-Analyse popup. While true we
  // stay on the create flow and poll the MOC status so each section unlocks as
  // the backend async jobs complete.
  const [isWatchingProgress, setIsWatchingProgress] = useState(false);
  // Populated when the Analyse POST reports the MOC already exists
  // (isDuplicate), so we can point the user to the existing record.
  const [duplicateMoc, setDuplicateMoc] = useState<{
    mocId: string;
    message: string;
  } | null>(null);

  const [getAdminDocuments, { data: adminDocsData }] =
    useLazyGetAdminDocumentsQuery();

  useEffect(() => {
    getAdminDocuments({
      params: {
        page: 1,
        size: 100,
        sort: "",
        dir: "",
        search: "",
        showDuplicates: false
      }
    });
  }, [getAdminDocuments]);

  const [getMocById, { data: mocDetail }] = useLazyGetMocByIdQuery();

  const [
    getMocEngineeringAnalysis,
    { data: engineeringAnalysis, isFetching: isAnalysisLoading }
  ] = useLazyGetMocEngineeringAnalysisQuery();

  const [
    getMocImpactAssessment,
    { data: impactAssessment, isFetching: isImpactAssessmentLoading }
  ] = useLazyGetMocImpactAssessmentQuery();

  const [
    getMocRiskAssessment,
    { data: riskAssessment, isFetching: isRiskAssessmentLoading }
  ] = useLazyGetMocRiskAssessmentQuery();

  const [
    getMocActionRegister,
    { data: actionRegister, isFetching: isActionRegisterLoading }
  ] = useLazyGetMocActionRegisterQuery();

  const [
    getMocSafeguards,
    { data: safeguards, isFetching: isSafeguardsLoading }
  ] = useLazyGetMocSafeguardsQuery();

  const [updateAssessmentStatus] = useUpdateAssessmentStatusMutation();

  // Update the local decision immediately, persist it via the status API, then
  // refetch the assessment so the row reflects the saved status.
  const handleAssessmentDecision = async (
    assessmentType: AssessmentType,
    assessmentId: string,
    decision: "accept" | "reject"
  ) => {
    setAssessmentDecisions((prev) => ({ ...prev, [assessmentId]: decision }));
    try {
      await updateAssessmentStatus({
        assessmentType,
        assessmentId,
        status: decision === "accept" ? "ACCEPTED" : "REJECTED"
      }).unwrap();
      if (!mocId) {
        return;
      }
      if (assessmentType === "moc_impacts") {
        getMocImpactAssessment(mocId);
      } else if (assessmentType === "moc_risks") {
        getMocRiskAssessment(mocId);
      } else if (assessmentType === "moc_actions") {
        getMocActionRegister(mocId);
      } else {
        getMocSafeguards(mocId);
      }
    } catch {
      // Keep the optimistic selection; the status API error is surfaced by RTK.
    }
  };

  // Decision column shared across all assessment tabs: shows the selected
  // status and the Accept / Reject buttons that persist it via the status API.
  const renderDecisionCell = (
    assessmentType: AssessmentType,
    id: string,
    stacked = false
  ) => {
    const decision = assessmentDecisions[id];
    return (
      <div className="moc-assess-decision-wrap">
        <div
          className={`moc-assess-decision${
            stacked ? " moc-assess-decision--stacked" : ""
          }`}
        >
          <button
            type="button"
            className={`moc-assess-decision__btn moc-assess-decision__btn--accept${
              decision === "accept" ? " moc-assess-decision__btn--active" : ""
            }`}
            onClick={() =>
              handleAssessmentDecision(assessmentType, id, "accept")
            }
          >
            <CheckCircleOutlineIcon fontSize="small" />
            <span>Accept</span>
          </button>
          <button
            type="button"
            className={`moc-assess-decision__btn moc-assess-decision__btn--reject${
              decision === "reject" ? " moc-assess-decision__btn--active" : ""
            }`}
            onClick={() =>
              handleAssessmentDecision(assessmentType, id, "reject")
            }
          >
            <CloseIcon fontSize="small" />
            <span>Reject</span>
          </button>
        </div>
      </div>
    );
  };

  // Ensures the saved MOC is applied to the form only once, so that after the
  // initial repopulation the user can freely edit without values reverting.
  const populatedMocIdRef = useRef<string | null>(null);

  // Load the full saved MOC so edit/view continues with all existing data,
  // not just the title carried in navigation state.
  useEffect(() => {
    if (editMoc?.mocId) {
      getMocById(editMoc.mocId);
    }
  }, [editMoc?.mocId, getMocById]);

  useEffect(() => {
    if (!mocDetail) {
      return;
    }
    const currentMocId = editMoc?.mocId ?? null;
    if (populatedMocIdRef.current === currentMocId) {
      return;
    }

    // Backend field naming/wrapping can vary; unwrap and read first non-empty.
    const raw = mocDetail as unknown as Record<string, unknown>;
    const detail = ((raw.data as Record<string, unknown>) ??
      (raw.moc as Record<string, unknown>) ??
      (raw.result as Record<string, unknown>) ??
      raw) as Record<string, unknown>;
    const pick = (...keys: string[]): string | undefined => {
      for (const key of keys) {
        const value = detail[key];
        if (value !== undefined && value !== null && value !== "") {
          return String(value);
        }
      }
      return undefined;
    };

    setForm((prev) => ({
      ...prev,
      mocTitle: pick("title", "mocTitle", "name") ?? prev.mocTitle,
      asset: pick("assetName", "assetId", "asset", "asset_name") ?? prev.asset,
      equipment: pick("equipment") ?? prev.equipment,
      trigger: pick("trigger") ?? prev.trigger,
      proposedChange:
        pick("description", "proposedChange", "proposed_change") ??
        prev.proposedChange,
      scheduleImpact:
        pick("scheduleImpactDays", "scheduleImpact", "schedule_impact_days") ??
        prev.scheduleImpact,
      financialImpact:
        pick("financialImpact", "financial_impact") ?? prev.financialImpact,
      businessJustification:
        pick(
          "businessJustification",
          "business_justification",
          "justification"
        ) ?? prev.businessJustification
    }));

    // Restore previously uploaded drawings/documents so they stay visible when
    // returning to the Initiate Change step. Split by file category.
    const uploaded = (detail.uploaded_documents ?? detail.uploadedDocuments) as
      | Array<Record<string, unknown>>
      | undefined;
    if (Array.isArray(uploaded)) {
      const toAssetDoc = (doc: Record<string, unknown>): AssetDoc => ({
        id: String(doc.documentGuidCode ?? doc.documentGuid ?? doc.id ?? ""),
        name: String(doc.name ?? doc.fileName ?? "Untitled"),
        date: "",
        source: "Processing Hub",
        size: ""
      });
      const isDrawing = (doc: Record<string, unknown>): boolean =>
        String(doc.fileCategory ?? "")
          .toLowerCase()
          .includes("drawing");
      setSelectedAssets({
        diagram: uploaded.filter(isDrawing).map(toAssetDoc),
        documents: uploaded.filter((doc) => !isDrawing(doc)).map(toAssetDoc)
      });
    }

    populatedMocIdRef.current = currentMocId;
  }, [mocDetail, editMoc?.mocId]);

  // Close the Select Engineering Assets modal on Escape.
  useEffect(() => {
    if (!modalOpen) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setModalOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [modalOpen]);

  // Build the asset library from uploaded documents (api/Admin/Documents),
  // split by the file_category set during upload.
  const assetLibrary = useMemo<Record<AssetTab, AssetDoc[]>>(() => {
    const docs: any[] = adminDocsData?.data || [];
    if (docs.length === 0) {
      return ASSET_LIBRARY;
    }

    const getCategory = (doc: any): string =>
      (
        doc.FileCategory ||
        doc.fileCategory ||
        doc.file_category ||
        doc.category ||
        ""
      )
        .toString()
        .toLowerCase();

    const isDrawing = (doc: any): boolean =>
      getCategory(doc).includes("drawing") ||
      (doc.name || "").toLowerCase().endsWith(".dwg");

    const toAsset = (doc: any): AssetDoc => ({
      id: (doc.documentGuid || doc.id || doc.name || "").toString(),
      name: doc.name || doc.gpName || "Untitled",
      date: doc.createdDate ? formattedDate(doc.createdDate) : "",
      source: "Processing Hub",
      size: doc.fileSize || doc.size || "",
      status: doc.similarityStatus || doc.taggingStatus || doc.status || ""
    });

    return {
      diagram: docs.filter((doc) => isDrawing(doc)).map(toAsset),
      documents: docs.filter((doc) => !isDrawing(doc)).map(toAsset)
    };
  }, [adminDocsData]);

  // AI engineering analysis metrics, falling back to placeholders until the
  // analysis API responds.
  const analysisMetrics = useMemo(() => {
    if (isAnalysisLoading || !engineeringAnalysis) {
      // Avoid flashing hardcoded placeholder or stale values while the real analysis loads.
      return ANALYSIS_METRICS.map((metric) => ({ ...metric, value: "—" }));
    }
    // Prefer the top-level confidence_score; fall back to the recommendation value.
    const confidence =
      engineeringAnalysis.confidenceScore ??
      engineeringAnalysis.recommendation.confidence;
    return [
      {
        label: "Engineering Confidence",
        value: confidence !== null ? `${Math.round(confidence)}%` : "—",
        highlight: true
      },
      {
        label: "Affected Systems",
        value: String(engineeringAnalysis.affectedSystems.length).padStart(
          2,
          "0"
        )
      },
      {
        label: "Linked References",
        value: String(engineeringAnalysis.linkedReferences.length).padStart(
          2,
          "0"
        )
      },
      {
        label: "MOC Required",
        value: engineeringAnalysis.isMocRequired ? "Yes" : "No"
      }
    ];
  }, [engineeringAnalysis, isAnalysisLoading]);

  // Map each affected system from the API to its own expandable row, keeping the
  // equipment name as the heading and its label/impact as the expandable detail.
  const affectedSystemsView = useMemo(() => {
    const systems = engineeringAnalysis?.affectedSystems || [];
    if (systems.length === 0) {
      return null;
    }
    return systems.map((system, index) => {
      const details: string[] = [];
      if (system.label) {
        details.push(`Category: ${system.label}`);
      }
      if (system.impact) {
        details.push(`Impact: ${system.impact}`);
      }
      return {
        id: system.id || `${system.name || "system"}-${index}`,
        name: system.name || system.label || "Unknown System",
        category: system.label,
        relatedTo: system.relatedTo,
        items: details
      };
    });
  }, [engineeringAnalysis]);

  const linkedReferencesView = engineeringAnalysis?.linkedReferences ?? null;
  const recommendationView = engineeringAnalysis?.recommendation ?? null;

  // When the AI analysis concludes no formal MOC is required, the workflow ends
  // at Engineering Analysis and later sections must stay locked.
  const isMocNotRequired =
    Boolean(engineeringAnalysis) &&
    engineeringAnalysis?.isMocRequired === false;

  // Align the applicability selection with the AI recommendation when it loads.
  useEffect(() => {
    if (!recommendationView) {
      return;
    }
    setSelectedMocOption(
      recommendationView.mocRequired ? "formal" : "not-required"
    );
  }, [recommendationView]);

  const handleChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // Financial impact is one string for the backend (e.g. "$100000"); split it
  // into a currency prefix + amount so the user picks the currency and types
  // the value, then recombine on change.
  const financialCurrency =
    CURRENCY_OPTIONS.find((symbol) =>
      form.financialImpact.startsWith(symbol)
    ) ?? CURRENCY_OPTIONS[0];
  const financialAmount = CURRENCY_OPTIONS.some((symbol) =>
    form.financialImpact.startsWith(symbol)
  )
    ? form.financialImpact.slice(financialCurrency.length)
    : form.financialImpact;

  const handleFinancialCurrencyChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const currency = event.target.value;
    setForm((prev) => ({
      ...prev,
      financialImpact: financialAmount ? `${currency}${financialAmount}` : ""
    }));
  };

  const handleFinancialAmountChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const amount = event.target.value;
    setForm((prev) => ({
      ...prev,
      financialImpact: amount ? `${financialCurrency}${amount}` : ""
    }));
  };

  // Schedule impact is one string for the backend (e.g. "2 Weeks"); split it
  // into an amount + unit. Handles a bare unit (no amount yet) so the unit
  // selection persists even before a value is typed.
  const scheduleMatch = form.scheduleImpact
    .trim()
    .match(/^(\d+(?:\.\d+)?)?\s*(hours|days|weeks)?$/i);
  const scheduleAmount = scheduleMatch
    ? scheduleMatch[1] ?? ""
    : form.scheduleImpact;
  const scheduleUnit =
    (scheduleMatch &&
      SCHEDULE_UNIT_OPTIONS.find(
        (unit) => unit.toLowerCase() === (scheduleMatch[2] ?? "").toLowerCase()
      )) ||
    SCHEDULE_UNIT_OPTIONS[0];

  const handleScheduleAmountChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const amount = event.target.value;
    setForm((prev) => ({
      ...prev,
      scheduleImpact: amount ? `${amount} ${scheduleUnit}` : scheduleUnit
    }));
  };

  const handleScheduleUnitChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const unit = event.target.value;
    setForm((prev) => ({
      ...prev,
      scheduleImpact: scheduleAmount ? `${scheduleAmount} ${unit}` : unit
    }));
  };

  const [mocId, setMocId] = useState<string | null>(editMoc?.mocId ?? null);
  const [reportGenerated, setReportGenerated] = useState(false);
  const [reportBlob, setReportBlob] = useState<Blob | null>(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [createMoc, { isLoading: isCreating }] = useCreateMocMutation();
  const [updateMoc, { isLoading: isUpdating }] = useUpdateMocMutation();
  const [generateFinalMocReport, { isLoading: isGeneratingReport }] =
    useGenerateFinalMocReportMutation();
  const [getMocDecisionPackage, { data: decisionPackage }] =
    useLazyGetMocDecisionPackageQuery();
  const [updateDecisionSummary, { isLoading: isSavingSummary }] =
    useUpdateDecisionSummaryMutation();
  const decision = useMemo(
    () => buildDecisionView(decisionPackage),
    [decisionPackage]
  );
  const [isEditingReco, setIsEditingReco] = useState(false);
  const [recoDraft, setRecoDraft] = useState({ title: "", text: "" });
  const [isEditingSummary, setIsEditingSummary] = useState(false);
  const [summaryDraft, setSummaryDraft] = useState<string[]>([]);
  const isSavingMoc = isCreating || isUpdating;

  // Initiate Change is complete only when all mandatory fields are filled and
  // both a diagram and at least one document have been uploaded.
  const diagramFiles = selectedAssets.diagram ?? [];
  const documentFiles = selectedAssets.documents ?? [];
  const isInitiateChangeComplete =
    form.mocTitle.trim() !== "" &&
    form.asset.trim() !== "" &&
    form.equipment.trim() !== "" &&
    form.trigger.trim() !== "" &&
    form.proposedChange.trim() !== "" &&
    diagramFiles.length > 0 &&
    documentFiles.length > 0;

  // Once the MOC has been analysed (submitted this session, reopened past the
  // Initiate Change step, or in view mode), the engineering drawings and
  // documents can no longer be changed.
  const isAssetSelectionLocked =
    isViewMode || analysisSubmitted || (locationState?.initialStep ?? 0) > 0;

  // The Analyse submit kicks off a chain of async backend jobs that move the MOC
  // through cumulative workflow stages. Read the latest stage from the polled
  // MOC detail (backend field naming/wrapping can vary) to know which sections
  // have data ready.
  const workflowStageValue = useMemo(() => {
    if (!mocDetail) {
      return "";
    }
    const raw = mocDetail as unknown as Record<string, unknown>;
    const detail = ((raw.data as Record<string, unknown>) ??
      (raw.moc as Record<string, unknown>) ??
      (raw.result as Record<string, unknown>) ??
      raw) as Record<string, unknown>;
    const stage =
      detail.currentStage ??
      detail.workflowStage ??
      detail.workflow_stage ??
      detail.stage;
    return String(stage ?? "")
      .toUpperCase()
      .replace(/\s+/g, "_");
  }, [mocDetail]);

  const stageHasReached = (marker: string): boolean =>
    workflowStageValue.includes(marker);
  const isWorkflowComplete = stageHasReached("MOC_COMPLETED");
  // Once analysis has completed the async pipeline is confirmed running, so the
  // later stages are on their way and we can safely gate their sections.
  const hasAnalysisCompleted =
    stageHasReached("ANALYSIS_COMPLETED") ||
    stageHasReached("ASSESSMENT_COMPLETED") ||
    stageHasReached("DECISION_PACKAGE") ||
    stageHasReached("PACKAGE_GENERATED") ||
    isWorkflowComplete;
  // Gate sections with a spinner both during the live "wait here" watch after
  // Analyse and when reopening an in-progress MOC from the list, so a section
  // whose stage hasn't completed shows a spinner instead of empty data. Stages
  // are cumulative, so a later stage implies the earlier ones are ready.
  const gateByStage = isWatchingProgress || hasAnalysisCompleted;
  const isAnalysisReady = !gateByStage || hasAnalysisCompleted;
  const isAssessmentReady =
    !gateByStage ||
    stageHasReached("ASSESSMENT_COMPLETED") ||
    stageHasReached("DECISION_PACKAGE") ||
    stageHasReached("PACKAGE_GENERATED") ||
    isWorkflowComplete;
  const isDecisionReady =
    !gateByStage ||
    stageHasReached("DECISION") ||
    stageHasReached("PACKAGE") ||
    stageHasReached("PACKING") ||
    isWorkflowComplete ||
    // The decision-packing-review data can be generated before the workflow
    // stage reaches its final decision marker, so once it has loaded we treat
    // the section as ready rather than keeping the spinner.
    Boolean(decisionPackage);
  const showAnalysisPending = !isAnalysisReady;
  const showAssessmentPending = !isAssessmentReady;
  const showDecisionPending = !isDecisionReady;

  // Poll the MOC status while any section is gated (live watch after Analyse, or
  // an in-progress MOC opened from the list) so sections unlock as the backend
  // async jobs finish. Stops once the workflow completes.
  useEffect(() => {
    if (!gateByStage || !mocId || isWorkflowComplete) {
      return;
    }
    const intervalId = window.setInterval(() => {
      getMocById(mocId);
    }, 12000);
    return () => window.clearInterval(intervalId);
  }, [gateByStage, mocId, isWorkflowComplete, getMocById]);

  // Load AI engineering analysis once the Engineering Analysis step is opened
  // for a saved MOC and its analysis stage has completed.
  useEffect(() => {
    if (activeStep === 1 && mocId && isAnalysisReady) {
      getMocEngineeringAnalysis(mocId);
    }
  }, [activeStep, mocId, isAnalysisReady, getMocEngineeringAnalysis]);

  // Load the decision package when the Decision Package step is opened. The data
  // can exist before the workflow reaches its final decision stage, so we don't
  // gate the fetch on stage; re-runs as the polled stage advances so it appears
  // as soon as it's generated.
  useEffect(() => {
    if (activeStep === 3 && mocId) {
      getMocDecisionPackage(mocId);
    }
  }, [activeStep, mocId, workflowStageValue, getMocDecisionPackage]);

  // Reflect an already-generated report from the decision package response.
  useEffect(() => {
    if (decisionPackage?.is_report_generated) {
      setReportGenerated(true);
    }
  }, [decisionPackage]);

  // Load the impact assessment once the Assessment Workspace step is opened and
  // its stage has completed.
  useEffect(() => {
    if (activeStep === 2 && mocId && isAssessmentReady) {
      getMocImpactAssessment(mocId);
    }
  }, [activeStep, mocId, isAssessmentReady, getMocImpactAssessment]);

  // Map the impact assessment response into table rows.
  const impactAssessmentItems = useMemo(
    () => impactAssessment?.items ?? [],
    [impactAssessment]
  );

  const impactAssessmentRows = useMemo<AssessmentRow[]>(
    () =>
      impactAssessmentItems.map((item) => ({
        id: item.impactId,
        dimension: formatImpactCategory(item.category),
        assessment: item.description,
        rationale: item.rationale,
        evidence: item.evidence,
        rating: normalizeImpactRating(item.severity)
      })),
    [impactAssessmentItems]
  );

  // Pre-select the engineer decision from the impact status returned by the API.
  useEffect(() => {
    setAssessmentDecisions((prev) => {
      const next = { ...prev };
      impactAssessmentItems.forEach((item) => {
        const status = (item.status || "").toUpperCase();
        if (status === "ACCEPTED") {
          next[item.impactId] = "accept";
        } else if (status === "REJECTED") {
          next[item.impactId] = "reject";
        }
      });
      return next;
    });
  }, [impactAssessmentItems]);

  // Load the risk assessment once the Assessment Workspace step is opened and
  // its stage has completed.
  useEffect(() => {
    if (activeStep === 2 && mocId && isAssessmentReady) {
      getMocRiskAssessment(mocId);
    }
  }, [activeStep, mocId, isAssessmentReady, getMocRiskAssessment]);

  // Map the risk assessment response into table rows.
  const riskAssessmentItems = useMemo(
    () => riskAssessment?.items ?? [],
    [riskAssessment]
  );

  const riskAssessmentRows = useMemo<RiskRow[]>(
    () =>
      riskAssessmentItems.map((item) => ({
        id: item.riskId,
        statement: item.description,
        consequence: item.consequenceDescription,
        safeguards: item.existingSafeguards,
        mitigation: item.mitigationPlan,
        severity: item.severity ?? 0,
        likelihood: item.likelihood ?? 0,
        initial: normalizeImpactRating(item.initialRisk),
        residual: normalizeImpactRating(item.riskRating || item.residualRisk)
      })),
    [riskAssessmentItems]
  );

  // Pre-select the engineer decision from the risk status returned by the API.
  useEffect(() => {
    setAssessmentDecisions((prev) => {
      const next = { ...prev };
      riskAssessmentItems.forEach((item) => {
        const status = (item.status || "").toUpperCase();
        if (status === "ACCEPTED") {
          next[item.riskId] = "accept";
        } else if (status === "REJECTED") {
          next[item.riskId] = "reject";
        }
      });
      return next;
    });
  }, [riskAssessmentItems]);

  // Load the action register once the Assessment Workspace step is opened and
  // its stage has completed.
  useEffect(() => {
    if (activeStep === 2 && mocId && isAssessmentReady) {
      getMocActionRegister(mocId);
    }
  }, [activeStep, mocId, isAssessmentReady, getMocActionRegister]);

  // Map the action register response into table rows.
  const actionRegisterItems = useMemo(
    () => actionRegister?.items ?? [],
    [actionRegister]
  );

  const actionRegisterRows = useMemo<ActionRow[]>(
    () =>
      actionRegisterItems.map((item) => ({
        id: item.actionId || item.actionCode,
        code: item.actionCode || item.actionId,
        action: item.description,
        owner: item.owner,
        dueStage: formatImpactCategory(item.dueStage),
        priority: normalizeImpactRating(item.priority),
        status:
          (item.status || "").toUpperCase() === "REJECTED"
            ? "Rejected"
            : "Accepted"
      })),
    [actionRegisterItems]
  );

  // KPI cards for the Assessment Workspace: Residual Risk is derived from the
  // average severity of the risk rows, and the third card shows the count of
  // action register records.
  const assessmentKpis = useMemo<AssessmentKpi[]>(() => {
    const severities = riskAssessmentRows
      .map((row) => Number(row.severity))
      .filter((value) => Number.isFinite(value));
    const avgSeverity =
      severities.length > 0
        ? severities.reduce((sum, value) => sum + value, 0) / severities.length
        : 0;

    let riskValue = "Low";
    let riskAccent: AssessmentKpi["accent"] = "green";
    if (avgSeverity > 4) {
      riskValue = "High";
      riskAccent = "red";
    } else if (avgSeverity > 2) {
      riskValue = "Medium";
      riskAccent = "orange";
    }

    return ASSESSMENT_KPIS.map((kpi) => {
      if (kpi.id === "risk") {
        return { ...kpi, value: riskValue, accent: riskAccent };
      }
      if (kpi.id === "systems") {
        return {
          ...kpi,
          label: "Action Count",
          value: String(actionRegisterRows.length),
          caption: "Action register records"
        };
      }
      if (kpi.id === "confidence") {
        const confidence = impactAssessment?.engineeringConfidenceScore;
        return {
          ...kpi,
          value: confidence != null ? `${Math.round(confidence)}%` : "-"
        };
      }
      return kpi;
    });
  }, [riskAssessmentRows, actionRegisterRows, impactAssessment]);

  // Pre-select the engineer decision from the action status returned by the API.
  useEffect(() => {
    setAssessmentDecisions((prev) => {
      const next = { ...prev };
      actionRegisterItems.forEach((item) => {
        const id = item.actionId || item.actionCode;
        const status = (item.status || "").toUpperCase();
        if (status.startsWith("ACCEPT")) {
          next[id] = "accept";
        } else if (status.startsWith("REJECT")) {
          next[id] = "reject";
        }
      });
      return next;
    });
  }, [actionRegisterItems]);

  // Load the safeguards once the Assessment Workspace step is opened and its
  // stage has completed.
  useEffect(() => {
    if (activeStep === 2 && mocId && isAssessmentReady) {
      getMocSafeguards(mocId);
    }
  }, [activeStep, mocId, isAssessmentReady, getMocSafeguards]);

  // Map the safeguards response into table rows.
  const safeguardItems = useMemo(() => safeguards?.items ?? [], [safeguards]);

  const safeguardRows = useMemo<SafeguardRow[]>(
    () =>
      safeguardItems.map((item) => ({
        id: item.safeguardId,
        hazard: item.impactHazardControlled,
        safeguard: item.safeguard,
        criticality: normalizeImpactRating(item.criticality),
        owner: item.owner
      })),
    [safeguardItems]
  );

  // Pre-select the engineer decision from the safeguard status returned by the API.
  useEffect(() => {
    setAssessmentDecisions((prev) => {
      const next = { ...prev };
      safeguardItems.forEach((item) => {
        const status = (item.status || "").toUpperCase();
        if (status.startsWith("ACCEPT")) {
          next[item.safeguardId] = "accept";
        } else if (status.startsWith("REJECT")) {
          next[item.safeguardId] = "reject";
        }
      });
      return next;
    });
  }, [safeguardItems]);

  const getSelectedDocumentGuidCodes = (
    assets: Record<AssetTab, AssetDoc[]> = selectedAssets
  ): string[] =>
    [
      ...assets.diagram.map((asset) => asset.id),
      ...assets.documents.map((asset) => asset.id)
    ].filter(Boolean);

  const buildMocPayload = (
    action: MocAction,
    assets: Record<AssetTab, AssetDoc[]> = selectedAssets
  ): MocRequest => ({
    title: form.mocTitle,
    description: form.proposedChange,
    assetId: form.asset,
    assetName: form.asset,
    equipment: form.equipment,
    trigger: form.trigger,
    priority: "Medium",
    scheduleImpactDays: form.scheduleImpact,
    financialImpact: form.financialImpact,
    businessJustification: form.businessJustification,
    action,
    documentGuidCodes: getSelectedDocumentGuidCodes(assets)
  });

  const saveMoc = async (
    action: MocAction,
    assets: Record<AssetTab, AssetDoc[]> = selectedAssets,
    method?: "POST" | "PUT"
  ) => {
    try {
      const payload = buildMocPayload(action, assets);
      // Analyse creates via POST; Save Draft updates via PUT. When no method is
      // forced, fall back to mocId (PUT if it exists, otherwise POST).
      const useUpdate = method ? method === "PUT" : Boolean(mocId);
      const result =
        useUpdate && mocId
          ? await updateMoc({ mocId, payload }).unwrap()
          : await createMoc(payload).unwrap();
      if (result?.mocId) {
        setMocId(result.mocId);
      }
      return result;
    } catch (error) {
      console.error("Failed to save MOC:", error);
      return null;
    }
  };

  const handleAnalyseClick = async () => {
    setAnalysisError(null);
    setAnalysisSubmitted(false);
    setIsAnalysing(true);
    // Show the popup while the submit POST is in flight. Once it completes the
    // popup stays open with options so the user can continue or go to the list.
    setShowAnalysisPopup(true);
    const result = await saveMoc("SUBMIT", selectedAssets);
    setIsAnalysing(false);
    if (!(result?.mocId ?? mocId)) {
      setShowAnalysisPopup(false);
      setAnalysisError(
        "We couldn't start the analysis. Please try again in a moment."
      );
      return;
    }
    // The backend matched an existing MOC with the same details — point the user
    // to it instead of continuing with a new analysis.
    if (result?.isDuplicate) {
      setShowAnalysisPopup(false);
      setDuplicateMoc({
        mocId: result.mocId,
        message:
          result.message ||
          "An MOC with the same details already exists. You can open it to review the existing analysis and assessment."
      });
      return;
    }
    setAnalysisSubmitted(true);
  };

  const handleGoToMocList = () => {
    setShowAnalysisPopup(false);
    navigate(MainRoutes.MANAGEMENT_OF_CHANGE);
  };

  // Dismiss the duplicate popup and take the user to the MOC list, pre-filtered
  // to the existing MOC id so they can open and review it.
  const handleViewDuplicateMoc = () => {
    const existing = duplicateMoc;
    setDuplicateMoc(null);
    navigate(MainRoutes.MANAGEMENT_OF_CHANGE, {
      state: existing ? { searchMocId: existing.mocId } : undefined
    });
  };

  // Stay on the create flow and watch the sections unlock as the backend jobs
  // finish. Kicks off an immediate status read; the polling effect keeps it
  // fresh until the workflow completes.
  const handleWaitForProgress = () => {
    setShowAnalysisPopup(false);
    setIsWatchingProgress(true);
    setActiveStep(1);
    if (mocId) {
      getMocById(mocId);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBackToInitiate = () => {
    setActiveStep(0);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleGenerateAssessment = () => {
    // No API call here — the Analyse submit already kicked off both async runs.
    // This only navigates to the Assessment Workspace, which shows the spinner
    // card until its data is ready.
    if (isMocNotRequired) {
      return;
    }
    setActiveStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleContinueToDecision = () => {
    // No API call — just navigate to the Decision Package, which shows the
    // spinner card until its data is ready.
    setActiveStep(3);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Turns a raw workflow stage (e.g. "ANALYSIS_COMPLETED") into a friendly label
  // for the processing cards shown while the backend jobs run.
  const formatStageLabel = (stage: string): string => {
    if (!stage) {
      return "Submitting";
    }
    return stage
      .toLowerCase()
      .split("_")
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // In-flow placeholder shown on a section whose backend data isn't ready yet
  // while the user waits after Analyse.
  const renderProcessingCard = (title: string, message: string) => (
    <section className="moc-info-card moc-processing">
      <div className="moc-processing__body">
        <CircularProgress size={44} />
        <h3 className="moc-processing__title">{title}</h3>
        <p className="moc-processing__desc">{message}</p>
        <span className="moc-processing__stage">
          Current stage: {formatStageLabel(workflowStageValue)}
        </span>
        <button
          type="button"
          className="moc-btn moc-btn--secondary"
          onClick={handleGoToMocList}
        >
          Go to MOC List
        </button>
      </div>
    </section>
  );

  // Builds a clean, formatted Decision Package PDF (Recommendation, Executive
  // Summary and the Impact/Risk/Safeguards/Actions cards) for the final report.
  const buildFinalReportPdf = (): Blob => {
    const sanitize = (text: string): string =>
      text
        .replace(/[\u2018\u2019]/g, "'")
        .replace(/[\u201C\u201D]/g, '"')
        .replace(/[\u2013]/g, "-")
        .replace(/[\u2014]/g, "--")
        .replace(/[\u2026]/g, "...")
        .replace(/[\u2022]/g, "-")
        // eslint-disable-next-line no-control-regex
        .replace(/[^\x20-\x7E\n]/g, "");

    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - margin * 2;
    let y = margin;

    const ensureSpace = (needed: number) => {
      if (y + needed > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
    };

    const sectionHeader = (title: string) => {
      ensureSpace(16);
      doc.setFillColor(46, 46, 56);
      doc.rect(margin, y, contentWidth, 9, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text(sanitize(title), margin + 3, y + 6);
      doc.setTextColor(46, 46, 56);
      y += 14;
    };

    const label = (text: string) => {
      ensureSpace(6);
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(107, 114, 128);
      doc.text(sanitize(text.toUpperCase()), margin, y);
      doc.setTextColor(46, 46, 56);
      y += 5;
    };

    const bodyText = (text: string) => {
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(46, 46, 56);
      const lines = doc.splitTextToSize(sanitize(text), contentWidth);
      lines.forEach((line: string) => {
        ensureSpace(5.5);
        doc.text(line, margin, y);
        y += 5;
      });
      y += 3;
    };

    const keyValueRow = (key: string, value: string) => {
      doc.setFontSize(10);
      doc.setTextColor(46, 46, 56);
      // Measure the value first (bold) so the key can wrap in the space left.
      doc.setFont("helvetica", "bold");
      const valueText = sanitize(value);
      const valueWidth = doc.getTextWidth(valueText);
      const gap = 6;
      const keyMaxWidth = Math.max(contentWidth - valueWidth - gap, 30);
      doc.setFont("helvetica", "normal");
      const keyLines: string[] = doc.splitTextToSize(
        sanitize(key),
        keyMaxWidth
      );
      const lineHeight = 5;
      ensureSpace((keyLines.length - 1) * lineHeight + 8);
      keyLines.forEach((line, index) => {
        doc.text(line, margin + 1, y + index * lineHeight);
      });
      doc.setFont("helvetica", "bold");
      doc.text(valueText, pageWidth - margin - 1, y, { align: "right" });
      const lastLineY = y + (keyLines.length - 1) * lineHeight;
      doc.setDrawColor(240, 239, 233);
      doc.line(margin, lastLineY + 1.5, pageWidth - margin, lastLineY + 1.5);
      y = lastLineY + 6.5;
    };

    // Title banner
    doc.setFillColor(46, 46, 56);
    doc.rect(0, 0, pageWidth, 22, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Final MOC Report", margin, 14);
    doc.setTextColor(46, 46, 56);
    y = 30;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(sanitize(`MOC ID: ${mocId || "-"}`), margin, y);
    y += 5;
    if (form.mocTitle) {
      const titleLines = doc.splitTextToSize(
        sanitize(`Title: ${form.mocTitle}`),
        contentWidth
      );
      titleLines.forEach((line: string) => {
        doc.text(line, margin, y);
        y += 5;
      });
    }
    y += 5;

    sectionHeader("AI Engineering Recommendation");
    label("Primary Recommendation");
    ensureSpace(9);
    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.text(sanitize(decision.recommendation.title), margin, y);
    y += 8;
    decision.recommendation.chips.forEach((chip) =>
      keyValueRow(chip.label, chip.value)
    );
    y += 3;
    bodyText(decision.recommendation.text);

    sectionHeader("AI Executive Summary");
    decision.executiveSummary.forEach((item) => {
      label(item.label);
      bodyText(item.text);
    });

    sectionHeader(`Impact Assessment - ${decision.impact.exposure}`);
    keyValueRow("Accepted", String(decision.impact.accepted));
    keyValueRow("Rejected", String(decision.impact.rejected));
    keyValueRow("Pending", String(decision.impact.pending));
    y += 4;

    sectionHeader(`Risk Assessment - ${decision.risk.residual}`);
    keyValueRow("Accepted", String(decision.risk.accepted));
    keyValueRow("Rejected", String(decision.risk.rejected));
    keyValueRow("Pending", String(decision.risk.pending));
    y += 4;

    sectionHeader(`Safeguards - ${decision.safeguards.score}% Score`);
    decision.safeguards.items.forEach((item) =>
      keyValueRow(item.name, titleCase(item.status))
    );
    y += 4;

    sectionHeader(`Engineering Actions - ${decision.actions.total} Total`);
    keyValueRow("Accepted", String(decision.actions.completed));
    keyValueRow("Pending", String(decision.actions.pending));
    keyValueRow("Rejected", String(decision.actions.overdue));

    return doc.output("blob");
  };

  const handlePreviewPdf = () => {
    try {
      const blob = buildFinalReportPdf();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      // Release the object URL once the preview tab has had time to load.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      console.error("Failed to preview MOC report:", error);
      ToastMessageUtil.show(
        "We couldn't open the report preview. Please try again.",
        ToastVariant.ERROR
      );
    }
  };

  const handleGenerateFinalReport = async () => {
    if (!mocId) {
      ToastMessageUtil.show(
        "Please save the MOC before generating the final report.",
        ToastVariant.WARNING
      );
      return;
    }
    try {
      const file = buildFinalReportPdf();
      await generateFinalMocReport({ mocId, file }).unwrap();
      setReportGenerated(true);
      setReportBlob(file);
      setShowReportModal(true);
      ToastMessageUtil.show(
        "Final MOC report is ready. Download it from the MOC list page.",
        ToastVariant.SUCCESS
      );
    } catch (error) {
      console.error("Failed to generate final MOC report:", error);
      ToastMessageUtil.show(
        "We couldn't generate the final MOC report. Please try again.",
        ToastVariant.ERROR
      );
    }
  };

  const handleDownloadReport = () => {
    const blob = reportBlob ?? buildFinalReportPdf();
    const safeName =
      (form.mocTitle || "MOC-Report").replace(/[^a-z0-9]+/gi, "-") ||
      "MOC-Report";
    const fileName = mocId ? `${safeName}-${mocId}.pdf` : `${safeName}.pdf`;
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const openEditReco = () => {
    setRecoDraft({
      title: decision.recommendation.title,
      text: decision.recommendation.text
    });
    setIsEditingReco(true);
  };

  const openEditSummary = () => {
    setSummaryDraft(decision.executiveSummary.map((item) => item.text));
    setIsEditingSummary(true);
  };

  const saveRecommendation = async () => {
    if (!mocId) {
      return;
    }
    try {
      await updateDecisionSummary({
        mocId,
        payload: {
          packageVersion: decisionPackage?.package?.packageVersion ?? 1,
          recommendation: {
            title: recoDraft.title,
            recommendation_text: recoDraft.text
          }
        }
      }).unwrap();
      setIsEditingReco(false);
      getMocDecisionPackage(mocId);
      ToastMessageUtil.show(
        "Recommendation updated successfully.",
        ToastVariant.SUCCESS
      );
    } catch (error) {
      console.error("Failed to update recommendation:", error);
      ToastMessageUtil.show(
        "We couldn't update the recommendation. Please try again.",
        ToastVariant.ERROR
      );
    }
  };

  const saveSummary = async () => {
    if (!mocId) {
      return;
    }
    const [
      businessObjective,
      engineeringImpact,
      residualRisk,
      complianceOutcome,
      implementationReadiness
    ] = summaryDraft;
    try {
      await updateDecisionSummary({
        mocId,
        payload: {
          packageVersion: decisionPackage?.package?.packageVersion ?? 1,
          executive_summary: {
            business_objective: businessObjective,
            engineering_impact: engineeringImpact,
            residual_risk: residualRisk,
            compliance_outcome: complianceOutcome,
            implementation_readiness: implementationReadiness
          }
        }
      }).unwrap();
      setIsEditingSummary(false);
      getMocDecisionPackage(mocId);
      ToastMessageUtil.show(
        "Executive summary updated successfully.",
        ToastVariant.SUCCESS
      );
    } catch (error) {
      console.error("Failed to update executive summary:", error);
      ToastMessageUtil.show(
        "We couldn't update the summary. Please try again.",
        ToastVariant.ERROR
      );
    }
  };

  const toggleAffectedSystem = (id: string) => {
    setExpandedSystem((prev) => (prev === id ? null : id));
  };

  const openAssetModal = (tab: AssetTab) => {
    setModalTab(tab);
    setDraftSelection({
      diagram: selectedAssets.diagram.map((asset) => asset.id),
      documents: selectedAssets.documents.map((asset) => asset.id)
    });
    setModalOpen(true);
  };

  const toggleDraftAsset = (tab: AssetTab, id: string) => {
    setDraftSelection((prev) => {
      // Engineering Drawing allows only a single selection
      if (tab === "diagram") {
        return {
          ...prev,
          diagram: prev.diagram.includes(id) ? [] : [id]
        };
      }
      const list = prev[tab];
      return {
        ...prev,
        [tab]: list.includes(id)
          ? list.filter((item) => item !== id)
          : [...list, id]
      };
    });
  };

  const handleAddDocuments = async () => {
    const nextSelected = {
      diagram: assetLibrary.diagram.filter((asset) =>
        draftSelection.diagram.includes(asset.id)
      ),
      documents: assetLibrary.documents.filter((asset) =>
        draftSelection.documents.includes(asset.id)
      )
    };
    // Only stage the selection locally; the MOC is created/updated (with these
    // documentGuidCodes) when the user clicks Save Draft or Analyse.
    setSelectedAssets(nextSelected);
    setModalOpen(false);
  };

  const handleRemoveAsset = (tab: AssetTab, id: string) => {
    setSelectedAssets((prev) => ({
      ...prev,
      [tab]: prev[tab].filter((asset) => asset.id !== id)
    }));
  };

  const draftDocs = [
    ...assetLibrary.diagram.filter((asset) =>
      draftSelection.diagram.includes(asset.id)
    ),
    ...assetLibrary.documents.filter((asset) =>
      draftSelection.documents.includes(asset.id)
    )
  ];

  return (
    <div className="moc-container">
      {/* Breadcrumb */}
      <nav className="moc-breadcrumb">
        <Link to="/home" className="breadcrumb-link">
          Home
        </Link>
        <span className="breadcrumb-separator">&gt;</span>
        <Link to={MainRoutes.MANAGEMENT_OF_CHANGE} className="breadcrumb-link">
          MOC
        </Link>
        <span className="breadcrumb-separator">&gt;</span>
        <span className="breadcrumb-current">
          {isViewMode ? "View MOC" : "New MOC"}
        </span>
      </nav>

      {/* Page header */}
      <div className="moc-page-header">
        <div className="moc-page-header__text">
          <h1 className="moc-page-title">
            {isViewMode
              ? "View Management of Change"
              : "Create New Management of Change"}
          </h1>
          <p className="moc-page-description">
            Initiate the technical change request. Provide precise facility data
            and business reasoning. Our AI will synthesize this data for
            technical impact analysis.
          </p>
        </div>
      </div>

      {/* Process flow / stepper */}
      <div className="moc-stepper">
        {STEPS.map((step, index) => (
          <React.Fragment key={step}>
            <div
              className={`moc-step${
                index === activeStep ? " moc-step--active" : ""
              }${index < activeStep ? " moc-step--completed" : ""}${
                isMocNotRequired && index > 1 ? " moc-step--disabled" : ""
              } moc-step--clickable`}
              onClick={() => {
                if (isMocNotRequired && index > 1) {
                  return;
                }
                setActiveStep(index);
              }}
            >
              <span className="moc-step__num">
                {index < activeStep ? (
                  <CheckCircleOutlineIcon fontSize="small" />
                ) : (
                  index + 1
                )}
              </span>
              <span className="moc-step__label">{step}</span>
            </div>
            {index < STEPS.length - 1 && (
              <span
                className={`moc-step__connector${
                  index <= activeStep ? " moc-step__connector--active" : ""
                }`}
              />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step content — Initiate Change */}
      <div className="moc-step-content">
        <fieldset
          className={`moc-step-fieldset${
            isViewMode ? " moc-step-fieldset--readonly" : ""
          }`}
        >
          {activeStep === 0 && (
            <>
              {/* MOC Information */}
              <section className="moc-info-card">
                <div className="moc-info-card__header">
                  <InfoOutlinedIcon
                    className="moc-info-card__icon"
                    fontSize="small"
                  />
                  <h3 className="moc-info-card__title">MOC Information</h3>
                  <span className="moc-info-card__tag moc-info-card__tag--required">
                    (Mandatory)
                  </span>
                </div>

                <div className="moc-field moc-field--full">
                  <label htmlFor="mocTitle" className="moc-field__label">
                    MOC Title
                  </label>
                  <input
                    id="mocTitle"
                    name="mocTitle"
                    type="text"
                    className="moc-field__input"
                    placeholder="e.g. Flare Header Valve Actuator Upgrade"
                    value={form.mocTitle}
                    onChange={handleChange}
                  />
                </div>

                <div className="moc-field-grid">
                  <div className="moc-field">
                    <label htmlFor="asset" className="moc-field__label">
                      Asset/Facility
                    </label>
                    <select
                      id="asset"
                      name="asset"
                      className="moc-field__input moc-field__select"
                      value={form.asset}
                      onChange={handleChange}
                    >
                      <option value="">Choose Asset...</option>
                      {/* Keep the saved asset visible even if it isn't one of
                          the predefined options (e.g. reopened MOCs). */}
                      {form.asset && !ASSET_OPTIONS.includes(form.asset) && (
                        <option value={form.asset}>{form.asset}</option>
                      )}
                      {ASSET_OPTIONS.map((asset) => (
                        <option key={asset} value={asset}>
                          {asset}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="moc-field">
                    <label htmlFor="equipment" className="moc-field__label">
                      Equipment
                    </label>
                    <input
                      id="equipment"
                      name="equipment"
                      type="text"
                      className="moc-field__input"
                      placeholder="Compressor K-101"
                      value={form.equipment}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="moc-field-grid">
                  <div className="moc-field">
                    <label htmlFor="trigger" className="moc-field__label">
                      Trigger
                    </label>
                    <select
                      id="trigger"
                      name="trigger"
                      className="moc-field__input moc-field__select"
                      value={form.trigger}
                      onChange={handleChange}
                    >
                      {TRIGGER_OPTIONS.map((trigger) => (
                        <option key={trigger} value={trigger}>
                          {trigger}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Proposed Change */}
                <div className="moc-subsection">
                  <ManageAccountsOutlinedIcon
                    className="moc-subsection__icon"
                    fontSize="small"
                  />
                  <h4 className="moc-subsection__title">Proposed Change</h4>
                </div>

                <div className="moc-field moc-field--full">
                  <textarea
                    id="proposedChange"
                    name="proposedChange"
                    className="moc-field__input moc-field__textarea"
                    placeholder="Provide a detailed technical description of the proposed modifications..."
                    rows={5}
                    value={form.proposedChange}
                    onChange={handleChange}
                  />
                </div>

                {/* AI Writing Guidance */}
                <div className="moc-ai-guidance">
                  <div className="moc-ai-guidance__header">
                    <AutoAwesomeOutlinedIcon
                      className="moc-ai-guidance__icon"
                      fontSize="small"
                    />
                    <span className="moc-ai-guidance__title">
                      AI Writing Guidance
                    </span>
                  </div>
                  <div className="moc-ai-guidance__grid">
                    {AI_GUIDANCE.map((item) => (
                      <div className="moc-ai-guidance__item" key={item}>
                        <CheckCircleOutlineIcon
                          className="moc-ai-guidance__check"
                          fontSize="small"
                        />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              {/* Business Impact & Justification */}
              <section className="moc-info-card">
                <div className="moc-info-card__header">
                  <BarChartOutlinedIcon
                    className="moc-info-card__icon"
                    fontSize="small"
                  />
                  <h3 className="moc-info-card__title">
                    Business Impact &amp; Justification
                  </h3>
                  <span className="moc-info-card__tag moc-info-card__tag--optional">
                    (Optional)
                  </span>
                </div>

                <div className="moc-field-grid">
                  <div className="moc-field">
                    <label
                      htmlFor="scheduleImpact"
                      className="moc-field__label"
                    >
                      Schedule Impact
                    </label>
                    <div className="moc-field__currency">
                      <input
                        id="scheduleImpact"
                        name="scheduleImpact"
                        type="text"
                        inputMode="numeric"
                        className="moc-field__input moc-field__currency-amount"
                        placeholder="2"
                        value={scheduleAmount}
                        onChange={handleScheduleAmountChange}
                      />
                      <select
                        aria-label="Schedule unit"
                        className="moc-field__input moc-field__select moc-field__unit-select"
                        value={scheduleUnit}
                        onChange={handleScheduleUnitChange}
                      >
                        {SCHEDULE_UNIT_OPTIONS.map((unit) => (
                          <option key={unit} value={unit}>
                            {unit}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="moc-field">
                    <label
                      htmlFor="financialImpact"
                      className="moc-field__label"
                    >
                      Financial Impact
                    </label>
                    <div className="moc-field__currency">
                      <select
                        aria-label="Currency"
                        className="moc-field__input moc-field__select moc-field__currency-select"
                        value={financialCurrency}
                        onChange={handleFinancialCurrencyChange}
                      >
                        {CURRENCY_OPTIONS.map((symbol) => (
                          <option key={symbol} value={symbol}>
                            {symbol}
                          </option>
                        ))}
                      </select>
                      <input
                        id="financialImpact"
                        name="financialImpact"
                        type="text"
                        inputMode="decimal"
                        className="moc-field__input moc-field__currency-amount"
                        placeholder="50,000"
                        value={financialAmount}
                        onChange={handleFinancialAmountChange}
                      />
                    </div>
                  </div>
                </div>

                <div className="moc-field moc-field--full">
                  <label
                    htmlFor="businessJustification"
                    className="moc-field__label"
                  >
                    Business Justification
                  </label>
                  <textarea
                    id="businessJustification"
                    name="businessJustification"
                    className="moc-field__input moc-field__textarea"
                    placeholder="Explain the underlying technical driver..."
                    rows={4}
                    value={form.businessJustification}
                    onChange={handleChange}
                  />
                </div>
              </section>

              {/* Engineering Diagram & Documents upload */}
              <div className="moc-doc-grid">
                {UPLOAD_SECTIONS.map((section) => {
                  const files = selectedAssets[section.key] ?? [];
                  const HeaderIcon =
                    section.key === "diagram"
                      ? DesignServicesOutlinedIcon
                      : FolderOpenOutlinedIcon;
                  return (
                    <section className="moc-upload-card" key={section.key}>
                      <div className="moc-upload-card__header">
                        <HeaderIcon className="moc-upload-card__icon" />
                        <h3 className="moc-upload-card__title">
                          {section.title}
                        </h3>
                        <span className="moc-upload-card__tag">
                          (Mandatory)
                        </span>
                      </div>

                      <p className="moc-upload-card__description">
                        {section.description}
                      </p>

                      <div className="moc-upload-card__actions">
                        <button
                          type="button"
                          className="moc-btn moc-btn--dark"
                          onClick={() => openAssetModal(section.key)}
                          disabled={isAssetSelectionLocked}
                        >
                          <AddIcon fontSize="small" />
                          <span>{section.buttonLabel}</span>
                        </button>
                      </div>

                      {files.length > 0 && (
                        <ul className="moc-upload-list">
                          {files.map((file) => (
                            <li className="moc-upload-list__item" key={file.id}>
                              <DescriptionOutlinedIcon className="moc-upload-list__file-icon" />
                              <span className="moc-upload-list__name">
                                {file.name}
                              </span>
                              <span className="moc-upload-list__size">
                                {file.size}
                              </span>
                              <button
                                type="button"
                                className="moc-upload-list__remove"
                                aria-label={`Remove ${file.name}`}
                                onClick={() =>
                                  handleRemoveAsset(section.key, file.id)
                                }
                                disabled={isAssetSelectionLocked}
                              >
                                <CloseIcon fontSize="small" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </section>
                  );
                })}
              </div>

              {/* Footer actions */}
              <div className="moc-step-actions">
                <button
                  type="button"
                  className="moc-btn moc-btn--secondary"
                  onClick={() => saveMoc("SAVE_DRAFT", selectedAssets, "PUT")}
                  disabled={isSavingMoc}
                >
                  {isSavingMoc ? "Saving..." : "Save Draft"}
                </button>
                <button
                  type="button"
                  className="moc-btn moc-btn--dark"
                  onClick={handleAnalyseClick}
                  disabled={
                    isSavingMoc || isAnalysing || !isInitiateChangeComplete
                  }
                  title={
                    isInitiateChangeComplete
                      ? undefined
                      : "Fill all mandatory fields and upload an engineering diagram and at least one document to continue."
                  }
                >
                  <span>{isAnalysing ? "Analysing..." : "Analyse"}</span>
                  <ArrowForwardIcon fontSize="small" />
                </button>
              </div>
            </>
          )}

          {activeStep === 1 &&
            (showAnalysisPending ? (
              renderProcessingCard(
                "Analysing your change",
                "AI is reviewing the uploaded drawings and cross-referencing the digital twin. This section will populate automatically once it's ready."
              )
            ) : (
              <>
                {/* AI Engineering Analysis */}
                <section className="moc-info-card">
                  <div className="moc-info-card__header">
                    <h3 className="moc-info-card__title">
                      AI Engineering Analysis
                    </h3>
                    <InfoOutlinedIcon
                      className="moc-analysis__header-info"
                      fontSize="small"
                    />
                  </div>
                  <p className="moc-analysis__intro">
                    AI has analyzed uploaded engineering drawings and
                    cross-referenced them with the digital twin model. Review
                    the generated intelligence before continuing to assessment.
                  </p>

                  <div className="moc-analysis__metrics">
                    {analysisMetrics.map((metric) => (
                      <div className="moc-analysis__metric" key={metric.label}>
                        <span className="moc-analysis__metric-label">
                          {metric.label}
                        </span>
                        <span
                          className={`moc-analysis__metric-value${
                            metric.highlight
                              ? " moc-analysis__metric-value--highlight"
                              : ""
                          }`}
                        >
                          {metric.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </section>

                {/* System Relationship Graph */}
                {engineeringAnalysis?.knowledgeGraph &&
                  engineeringAnalysis.knowledgeGraph.nodes.length > 0 && (
                    <section className="moc-info-card">
                      <div className="moc-info-card__header">
                        <h3 className="moc-info-card__title">
                          System Relationship Graph
                        </h3>
                      </div>

                      <div className="moc-kg">
                        <GraphCanvas
                          nodes={engineeringAnalysis.knowledgeGraph.nodes}
                          edges={engineeringAnalysis.knowledgeGraph.edges}
                          selectedNodeId={selectedGraphNodeId}
                          onNodeSelect={setSelectedGraphNodeId}
                          highlightedNodeIds={
                            engineeringAnalysis.knowledgeGraph.affectedNodeIds
                          }
                          scrollZoom
                        />
                        {engineeringAnalysis.knowledgeGraph.affectedNodeIds
                          .length > 0 && (
                          <div className="moc-kg__legend">
                            <span className="moc-kg__legend-dot" />
                            <span>Affected system</span>
                          </div>
                        )}
                      </div>
                    </section>
                  )}

                {/* Affected Systems */}
                <section className="moc-info-card">
                  <div className="moc-info-card__header">
                    <h3 className="moc-info-card__title">Affected Systems</h3>
                  </div>

                  <div className="moc-affected">
                    {affectedSystemsView && affectedSystemsView.length > 0 ? (
                      affectedSystemsView.map((system) => {
                        const isOpen = expandedSystem === system.id;
                        const category = system.category;
                        const relatedTo = system.relatedTo;
                        return (
                          <div
                            className={`moc-affected__item${
                              isOpen ? " moc-affected__item--open" : ""
                            }`}
                            key={system.id}
                          >
                            <button
                              type="button"
                              className="moc-affected__head"
                              onClick={() => toggleAffectedSystem(system.id)}
                              aria-expanded={isOpen}
                            >
                              <span className="moc-affected__name">
                                {system.name}
                              </span>
                              {relatedTo && (
                                <span className="moc-affected__related">
                                  Related to: {relatedTo}
                                </span>
                              )}
                              {category && (
                                <span className="moc-impact moc-impact--category">
                                  {category}
                                </span>
                              )}
                            </button>
                            {isOpen && (
                              <ul className="moc-affected__list">
                                {system.items.map((item) => (
                                  <li
                                    className="moc-affected__list-item"
                                    key={item}
                                  >
                                    {item}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <p className="moc-empty-state">
                        No affected systems found.
                      </p>
                    )}
                  </div>
                </section>

                {/* Linked Engineering References */}
                <section className="moc-info-card">
                  <div className="moc-info-card__header">
                    <h3 className="moc-info-card__title">
                      Linked Engineering References
                    </h3>
                  </div>

                  <table className="moc-ref-table">
                    <thead>
                      <tr>
                        <th>Document Name</th>
                        <th>Type</th>
                        <th>Revision</th>
                        <th>Status</th>
                        <th>Link</th>
                      </tr>
                    </thead>
                    <tbody>
                      {linkedReferencesView &&
                      linkedReferencesView.length > 0 ? (
                        linkedReferencesView.map((ref) => (
                          <tr key={ref.documentId}>
                            <td className="moc-ref-table__id">
                              {ref.documentName}
                            </td>
                            <td>{ref.type}</td>
                            <td>—</td>
                            <td>—</td>
                            <td>
                              <button
                                type="button"
                                className="moc-ref-table__link"
                                aria-label={`Open ${ref.documentName}`}
                              >
                                <OpenInNewIcon fontSize="small" />
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td className="moc-ref-table__empty" colSpan={5}>
                            No linked engineering references found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </section>

                {/* MOC Applicability Recommendation */}
                <div className="moc-applicability">
                  <h3 className="moc-applicability__title">
                    MOC Applicability Recommendation
                  </h3>
                  <div className="moc-applicability__options">
                    {MOC_OPTIONS.map((option) => (
                      <button
                        type="button"
                        key={option.id}
                        className={`moc-applicability__card${
                          selectedMocOption === option.id
                            ? " moc-applicability__card--active"
                            : ""
                        }`}
                        onClick={() => setSelectedMocOption(option.id)}
                        aria-pressed={selectedMocOption === option.id}
                      >
                        <span className="moc-applicability__card-title">
                          {option.title}
                        </span>
                        <span className="moc-applicability__card-desc">
                          {option.description}
                        </span>
                      </button>
                    ))}
                  </div>

                  {/* Recommendation banner */}
                  {recommendationView && (
                    <div className="moc-recommendation">
                      <div className="moc-recommendation__shield">
                        <ShieldOutlinedIcon />
                      </div>
                      <div className="moc-recommendation__body">
                        {recommendationView.confidence !== null && (
                          <span className="moc-recommendation__confidence">
                            Confidence :{" "}
                            {Math.round(recommendationView.confidence)}%
                          </span>
                        )}
                        <h4 className="moc-recommendation__heading">
                          {recommendationView.mocRequired
                            ? "Formal MOC Required"
                            : "MOC Not Required"}
                        </h4>
                        {engineeringAnalysis?.summary && (
                          <p className="moc-recommendation__desc">
                            {engineeringAnalysis.summary}
                          </p>
                        )}

                        {recommendationView.reasoning.length > 0 && (
                          <>
                            <span className="moc-recommendation__reasons-title">
                              Reason For Recommendation
                            </span>
                            <div className="moc-recommendation__reasons">
                              {recommendationView.reasoning.map((reason) => (
                                <div
                                  className="moc-recommendation__reason"
                                  key={reason}
                                >
                                  <CheckCircleOutlineIcon
                                    className="moc-recommendation__reason-check"
                                    fontSize="small"
                                  />
                                  <span>{reason}</span>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer actions */}
                <div className="moc-step-actions moc-step-actions--split">
                  <button
                    type="button"
                    className="moc-btn moc-btn--secondary"
                    onClick={handleBackToInitiate}
                  >
                    <ArrowBackIcon fontSize="small" />
                    <span>Back</span>
                  </button>
                  <div className="moc-step-actions__right">
                    <button
                      type="button"
                      className="moc-btn moc-btn--dark"
                      onClick={handleGenerateAssessment}
                      disabled={isMocNotRequired}
                      title={
                        isMocNotRequired
                          ? "A formal MOC is not required for this change, so no further assessment is needed."
                          : undefined
                      }
                    >
                      <span>Generate Engineering Assessment</span>
                      <ArrowForwardIcon fontSize="small" />
                    </button>
                  </div>
                </div>
              </>
            ))}

          {activeStep === 2 &&
            (showAssessmentPending ? (
              renderProcessingCard(
                "Generating engineering assessment",
                "AI is assessing impact, risk, safeguards and actions for your change. This section will populate automatically once it's ready."
              )
            ) : (
              <section className="moc-assess">
                <div className="moc-assess__panel">
                  <div className="moc-assess__heading">
                    <div className="moc-assess__title-row">
                      <h2 className="moc-assess__title">
                        Assessment Workspace
                      </h2>
                      <InfoOutlinedIcon
                        className="moc-assess__info"
                        fontSize="small"
                      />
                    </div>
                    <p className="moc-assess__subtitle">
                      Analyze the proposed change across impact, risk and
                      compliance dimensions. Validate safeguards and identify
                      actions to ensure a safe and compliant implementation.
                    </p>
                  </div>

                  <div className="moc-assess__kpis">
                    {assessmentKpis.map((kpi) => (
                      <div className="moc-assess-kpi" key={kpi.id}>
                        <span className="moc-assess-kpi__label">
                          {kpi.label}
                        </span>
                        <div className="moc-assess-kpi__value-row">
                          <span
                            className={`moc-assess-kpi__value moc-assess-kpi__value--${kpi.accent}`}
                          >
                            {kpi.value}
                          </span>
                          <span className="moc-assess-kpi__unit">
                            {kpi.unit}
                          </span>
                        </div>
                        <span className="moc-assess-kpi__caption">
                          {kpi.caption}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="moc-assess__tabs">
                  {ASSESSMENT_TABS.map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      className={`moc-assess__tab${
                        activeAssessmentTab === tab
                          ? " moc-assess__tab--active"
                          : ""
                      }`}
                      onClick={() => setActiveAssessmentTab(tab)}
                    >
                      {tab}
                    </button>
                  ))}
                </div>

                {activeAssessmentTab === "Impact Assessment" && (
                  <div className="moc-assess-table-wrap">
                    <table className="moc-assess-table">
                      <thead>
                        <tr>
                          <th>DIMENSION</th>
                          <th>IMPACT ASSESSMENT</th>
                          <th>SEVERITY LEVEL</th>
                          <th>RATING RATIONALE</th>
                          <th>ENGINEER DECISION</th>
                        </tr>
                      </thead>
                      <tbody>
                        {impactAssessmentRows.map((row) => (
                          <tr key={row.id}>
                            <td className="moc-assess-table__dim">
                              {row.dimension}
                            </td>
                            <td className="moc-assess-table__assessment">
                              {row.assessment}
                            </td>
                            <td>
                              <span
                                className={`moc-assess-rating moc-assess-rating--${row.rating.toLowerCase()}`}
                              >
                                {row.rating}
                              </span>
                            </td>
                            <td className="moc-assess-table__rationale">
                              {row.rationale}
                            </td>
                            <td>{renderDecisionCell("moc_impacts", row.id)}</td>
                          </tr>
                        ))}
                        {impactAssessmentRows.length === 0 && (
                          <tr>
                            <td colSpan={5} className="moc-assess-empty-cell">
                              {isImpactAssessmentLoading
                                ? "Loading impact assessment..."
                                : "No impact assessment data available yet."}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                {activeAssessmentTab === "Risk Assessment" && (
                  <div className="moc-assess-table-wrap">
                    <table className="moc-assess-table moc-assess-table--risk">
                      <thead>
                        <tr>
                          <th>RISK STATEMENT</th>
                          <th>CONSEQUENCE</th>
                          <th>AI MITIGATION</th>
                          <th>SEVERITY</th>
                          <th>LIKELIHOOD</th>
                          <th>RISK RATING</th>
                          <th>ACTION</th>
                        </tr>
                      </thead>
                      <tbody>
                        {riskAssessmentRows.map((row) => (
                          <tr key={row.id}>
                            <td className="moc-assess-table__dim">
                              {row.statement}
                            </td>
                            <td className="moc-assess-table__rationale">
                              {row.consequence}
                            </td>
                            <td className="moc-assess-table__rationale">
                              {row.mitigation}
                            </td>
                            <td>
                              <span className="moc-assess-metric">
                                {row.severity}
                              </span>
                            </td>
                            <td>
                              <span className="moc-assess-metric">
                                {row.likelihood}
                              </span>
                            </td>
                            <td>
                              <span
                                className={`moc-assess-rating moc-assess-rating--${row.residual.toLowerCase()}`}
                              >
                                {row.residual}
                              </span>
                            </td>
                            <td>
                              {renderDecisionCell("moc_risks", row.id, true)}
                            </td>
                          </tr>
                        ))}
                        {riskAssessmentRows.length === 0 && (
                          <tr>
                            <td colSpan={7} className="moc-assess-empty-cell">
                              {isRiskAssessmentLoading
                                ? "Loading risk assessment..."
                                : "No risk assessment data available yet."}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                    <div className="moc-assess-table__foot">
                      Showing {riskAssessmentRows.length} of{" "}
                      {riskAssessmentRows.length} Identified Risks
                    </div>
                  </div>
                )}

                {activeAssessmentTab === "Action Register" && (
                  <div className="moc-assess-table-wrap">
                    <table className="moc-assess-table moc-assess-table--action">
                      <thead>
                        <tr>
                          <th>ENGINEERING ACTION</th>
                          <th>OWNER</th>
                          <th>DUE STAGE</th>
                          <th>STATUS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {actionRegisterRows.map((row) => (
                          <tr key={row.id}>
                            <td
                              className={
                                row.highlighted
                                  ? "moc-assess-table__dim"
                                  : undefined
                              }
                            >
                              {row.action}
                            </td>
                            <td className="moc-assess-table__rationale">
                              {row.owner}
                            </td>
                            <td className="moc-assess-table__rationale">
                              {row.dueStage}
                            </td>
                            <td>{renderDecisionCell("moc_actions", row.id)}</td>
                          </tr>
                        ))}
                        {actionRegisterRows.length === 0 && (
                          <tr>
                            <td colSpan={4} className="moc-assess-empty-cell">
                              {isActionRegisterLoading
                                ? "Loading action register..."
                                : "No action register data available yet."}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                {activeAssessmentTab === "Safeguards" && (
                  <div className="moc-assess-table-wrap">
                    <table className="moc-assess-table moc-assess-table--safeguard">
                      <thead>
                        <tr>
                          <th>IMPACT / HAZARD CONTROLLED</th>
                          <th>SAFEGUARD</th>
                          <th>CRITICALITY</th>
                          <th>OWNER</th>
                          <th>ENGINEER DECISION</th>
                        </tr>
                      </thead>
                      <tbody>
                        {safeguardRows.map((row) => (
                          <tr key={row.id}>
                            <td className="moc-assess-table__dim">
                              {row.hazard}
                            </td>
                            <td className="moc-assess-table__rationale">
                              {row.safeguard}
                            </td>
                            <td>
                              <span
                                className={`moc-assess-rating moc-assess-rating--${row.criticality.toLowerCase()}`}
                              >
                                {row.criticality}
                              </span>
                            </td>
                            <td className="moc-assess-table__rationale">
                              {row.owner}
                            </td>
                            <td>
                              {renderDecisionCell("moc_safeguards", row.id)}
                            </td>
                          </tr>
                        ))}
                        {safeguardRows.length === 0 && (
                          <tr>
                            <td colSpan={5} className="moc-assess-empty-cell">
                              {isSafeguardsLoading
                                ? "Loading safeguards..."
                                : "No safeguards data available yet."}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="moc-assess__footer">
                  <button
                    type="button"
                    className="moc-btn moc-btn--dark"
                    onClick={handleContinueToDecision}
                  >
                    <span>Continue to Decision Page</span>
                    <ArrowForwardIcon fontSize="small" />
                  </button>
                </div>
              </section>
            ))}

          {activeStep === 3 &&
            (showDecisionPending ? (
              renderProcessingCard(
                "Preparing decision package",
                "AI is consolidating findings and recommendations into the decision package. It'll appear here as soon as it's generated."
              )
            ) : (
              <section className="moc-decision">
                <div className="moc-decision__panel">
                  <div className="moc-assess__heading">
                    <div className="moc-assess__title-row">
                      <h2 className="moc-assess__title">Decision Packaging</h2>
                      <InfoOutlinedIcon
                        className="moc-assess__info"
                        fontSize="small"
                      />
                    </div>
                    <p className="moc-assess__subtitle">
                      Consolidate all findings, recommendations and validations.
                      Review the final package to ensure completeness before
                      approval and implementation.
                    </p>
                  </div>
                  <div className="moc-decision__kpis">
                    {decision.kpis.map((kpi) => (
                      <div className="moc-decision-kpi" key={kpi.id}>
                        <span className="moc-decision-kpi__label">
                          {kpi.label}
                        </span>
                        <span
                          className={`moc-decision-kpi__value moc-decision-kpi__value--${kpi.accent}`}
                        >
                          {kpi.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="moc-decision__panel moc-decision-reco">
                  <div className="moc-decision-reco__head">
                    <AutoAwesomeOutlinedIcon
                      className="moc-decision-reco__spark"
                      fontSize="small"
                    />
                    <h3 className="moc-decision-reco__title">
                      AI Engineering Recommendation
                    </h3>
                  </div>
                  <span className="moc-decision-reco__eyebrow">
                    PRIMARY RECOMMENDATION
                  </span>
                  {isEditingReco ? (
                    <div className="moc-decision-edit">
                      <label className="moc-decision-edit__label">
                        Recommendation Title
                      </label>
                      <input
                        className="moc-decision-edit__input"
                        value={recoDraft.title}
                        onChange={(event) =>
                          setRecoDraft((prev) => ({
                            ...prev,
                            title: event.target.value
                          }))
                        }
                      />
                      <label className="moc-decision-edit__label">
                        Recommendation
                      </label>
                      <textarea
                        className="moc-decision-edit__textarea"
                        rows={5}
                        value={recoDraft.text}
                        onChange={(event) =>
                          setRecoDraft((prev) => ({
                            ...prev,
                            text: event.target.value
                          }))
                        }
                      />
                      <div className="moc-decision-reco__chips">
                        {decision.recommendation.chips.map((chip) => (
                          <span className="moc-decision-chip" key={chip.label}>
                            {chip.label}: <strong>{chip.value}</strong>
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <>
                      <h2 className="moc-decision-reco__headline">
                        {decision.recommendation.title}
                      </h2>
                      <div className="moc-decision-reco__chips">
                        {decision.recommendation.chips.map((chip) => (
                          <span className="moc-decision-chip" key={chip.label}>
                            {chip.label}: <strong>{chip.value}</strong>
                          </span>
                        ))}
                      </div>
                      <p className="moc-decision-reco__text">
                        {decision.recommendation.text}
                      </p>
                    </>
                  )}
                  <div className="moc-decision-reco__actions">
                    {isEditingReco ? (
                      <>
                        <button
                          type="button"
                          className="moc-btn moc-btn--dark"
                          onClick={saveRecommendation}
                          disabled={isSavingSummary}
                        >
                          <span>{isSavingSummary ? "Saving..." : "Save"}</span>
                        </button>
                        <button
                          type="button"
                          className="moc-btn moc-btn--secondary"
                          onClick={() => setIsEditingReco(false)}
                        >
                          <span>Cancel</span>
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="moc-btn moc-btn--secondary"
                        onClick={openEditReco}
                      >
                        <EditOutlinedIcon fontSize="small" />
                        <span>Edit Recommendation</span>
                      </button>
                    )}
                  </div>
                </div>

                <div className="moc-decision-exec">
                  <div className="moc-decision-exec__head">
                    <h3 className="moc-decision-exec__title">
                      AI Executive Summary
                    </h3>
                  </div>
                  <div className="moc-decision-exec__body">
                    <div className="moc-decision-exec__grid">
                      {decision.executiveSummary.map((item, index) => (
                        <div
                          className="moc-decision-exec__item"
                          key={item.label}
                        >
                          <span className="moc-decision-exec__label">
                            {item.label}
                          </span>
                          {isEditingSummary ? (
                            <textarea
                              className="moc-decision-edit__textarea"
                              rows={3}
                              value={summaryDraft[index] ?? ""}
                              onChange={(event) => {
                                const value = event.target.value;
                                setSummaryDraft((prev) => {
                                  const next = [...prev];
                                  next[index] = value;
                                  return next;
                                });
                              }}
                            />
                          ) : (
                            <p className="moc-decision-exec__text">
                              {item.text}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="moc-decision-exec__meta">
                      <span>
                        ENGINEERING SOURCES: {decision.sources.documents} DOCS
                      </span>
                      <span>
                        HISTORICAL MOCS: {decision.sources.historical}{" "}
                        REFERENCED
                      </span>
                      <span>STANDARDS: {decision.sources.standards}</span>
                    </div>
                    <div className="moc-decision-exec__foot">
                      <span className="moc-decision-exec__gen">
                        {decision.generatedOn}
                      </span>
                      <div className="moc-decision-exec__actions">
                        {isEditingSummary ? (
                          <>
                            <button
                              type="button"
                              className="moc-btn moc-btn--primary"
                              onClick={saveSummary}
                              disabled={isSavingSummary}
                            >
                              {isSavingSummary ? "Saving..." : "Save"}
                            </button>
                            <button
                              type="button"
                              className="moc-btn moc-btn--secondary"
                              onClick={() => setIsEditingSummary(false)}
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            className="moc-btn moc-btn--primary"
                            onClick={openEditSummary}
                          >
                            Edit Summary
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="moc-decision-cards">
                  <div className="moc-decision-card">
                    <div className="moc-decision-card__head">
                      <span className="moc-decision-card__eyebrow">
                        IMPACT ASSESSMENT
                      </span>
                      <WarningAmberOutlinedIcon
                        className="moc-decision-card__icon moc-decision-card__icon--red"
                        fontSize="small"
                      />
                    </div>
                    <span className="moc-decision-card__value moc-decision-card__value--red">
                      {decision.impact.exposure}
                    </span>
                    <div className="moc-decision-card__rows">
                      <div className="moc-decision-card__row">
                        <span>Accepted</span>
                        <strong>{decision.impact.accepted}</strong>
                      </div>
                      <div className="moc-decision-card__row">
                        <span>Rejected</span>
                        <strong>{decision.impact.rejected}</strong>
                      </div>
                      <div className="moc-decision-card__row">
                        <span>Pending</span>
                        <strong>{decision.impact.pending}</strong>
                      </div>
                    </div>
                  </div>

                  <div className="moc-decision-card">
                    <div className="moc-decision-card__head">
                      <span className="moc-decision-card__eyebrow">
                        RISK ASSESSMENT
                      </span>
                      <ShieldOutlinedIcon
                        className="moc-decision-card__icon moc-decision-card__icon--orange"
                        fontSize="small"
                      />
                    </div>
                    <span className="moc-decision-card__value moc-decision-card__value--orange">
                      {decision.risk.residual}
                    </span>
                    <div className="moc-decision-card__rows">
                      <div className="moc-decision-card__row">
                        <span>Accepted</span>
                        <strong>{decision.risk.accepted}</strong>
                      </div>
                      <div className="moc-decision-card__row">
                        <span>Rejected</span>
                        <strong>{decision.risk.rejected}</strong>
                      </div>
                      <div className="moc-decision-card__row">
                        <span>Pending</span>
                        <strong>{decision.risk.pending}</strong>
                      </div>
                    </div>
                  </div>

                  <div className="moc-decision-card">
                    <div className="moc-decision-card__head">
                      <span className="moc-decision-card__eyebrow">
                        SAFEGUARDS
                      </span>
                      <InfoOutlinedIcon
                        className="moc-decision-card__icon moc-decision-card__icon--green"
                        fontSize="small"
                      />
                    </div>
                    <span className="moc-decision-card__value moc-decision-card__value--green">
                      {decision.safeguards.score}% Score
                    </span>
                    <ul className="moc-decision-card__list">
                      {decision.safeguards.items.map((item) => {
                        const isComplete =
                          item.status.toUpperCase() === "COMPLETED";
                        return (
                          <li key={item.name}>
                            {isComplete ? (
                              <CheckCircleOutlineIcon
                                className="moc-decision-check"
                                fontSize="small"
                              />
                            ) : (
                              <WarningAmberOutlinedIcon
                                className="moc-decision-warn"
                                fontSize="small"
                              />
                            )}
                            {item.name}
                            {isComplete ? "" : ` (${titleCase(item.status)})`}
                          </li>
                        );
                      })}
                    </ul>
                  </div>

                  <div className="moc-decision-card moc-decision-card--actions">
                    <div className="moc-decision-card__head">
                      <span className="moc-decision-card__eyebrow">
                        ENGINEERING ACTIONS
                      </span>
                    </div>
                    <span className="moc-decision-card__value">
                      {decision.actions.total} Total
                    </span>
                    <div className="moc-decision-actions-stats">
                      <div className="moc-decision-actions-stat">
                        <strong className="moc-decision-stat--green">
                          {decision.actions.completed}
                        </strong>
                        <span>ACCEPTED</span>
                      </div>
                      <div className="moc-decision-actions-stat">
                        <strong className="moc-decision-stat--orange">
                          {decision.actions.pending}
                        </strong>
                        <span>PENDING</span>
                      </div>
                      <div className="moc-decision-actions-stat">
                        <strong className="moc-decision-stat--red">
                          {decision.actions.overdue}
                        </strong>
                        <span>REJECTED</span>
                      </div>
                    </div>
                    <div className="moc-decision-actions-progress">
                      <div className="moc-decision-actions-progress__head">
                        <span>Completion</span>
                        <span>
                          {decision.actions.total
                            ? Math.round(
                                (decision.actions.completed /
                                  decision.actions.total) *
                                  100
                              )
                            : 0}
                          %
                        </span>
                      </div>
                      <div className="moc-decision-actions-progress__track">
                        <div
                          className="moc-decision-actions-progress__fill"
                          style={{
                            width: `${
                              decision.actions.total
                                ? Math.round(
                                    (decision.actions.completed /
                                      decision.actions.total) *
                                      100
                                  )
                                : 0
                            }%`
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="moc-decision__panel moc-decision-checklist">
                  <span className="moc-decision-checklist__title">
                    PACKAGE VALIDATION CHECKLIST
                  </span>
                  <div className="moc-decision-checklist__grid">
                    {VALIDATION_CHECKLIST.map((item) => (
                      <div className="moc-decision-checklist__item" key={item}>
                        <CheckCircleOutlineIcon fontSize="small" />
                        {item}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Footer actions */}
                <div className="moc-step-actions moc-step-actions--split">
                  <button
                    type="button"
                    className="moc-btn moc-btn--secondary"
                    onClick={() => setActiveStep(2)}
                  >
                    <ArrowBackIcon fontSize="small" />
                    <span>Back</span>
                  </button>
                  <div className="moc-step-actions__right">
                    <Link
                      to={MainRoutes.MANAGEMENT_OF_CHANGE}
                      className="moc-btn moc-btn--secondary"
                    >
                      <span>Back to Dashboard</span>
                    </Link>
                    {/* <button type="button" className="moc-btn moc-btn--secondary">
                    <span>Save Draft</span>
                  </button> */}
                    {/* <button type="button" className="moc-btn moc-btn--secondary">
                    <span>Export Summary</span>
                  </button> */}
                    <button
                      type="button"
                      className="moc-btn moc-btn--secondary"
                      onClick={handlePreviewPdf}
                    >
                      <span>Preview PDF</span>
                    </button>
                    <button
                      type="button"
                      className="moc-btn moc-btn--secondary"
                    >
                      <span>Send to SME</span>
                    </button>
                    {reportGenerated ? (
                      <button
                        type="button"
                        className="moc-btn moc-btn--dark"
                        onClick={() => setShowReportModal(true)}
                      >
                        <FileDownloadOutlinedIcon fontSize="small" />
                        <span>Download Report</span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="moc-btn moc-btn--dark"
                        onClick={handleGenerateFinalReport}
                        disabled={isGeneratingReport}
                      >
                        <span>
                          {isGeneratingReport
                            ? "Generating Report..."
                            : "Generate Final MOC Report"}
                        </span>
                        <ArrowForwardIcon fontSize="small" />
                      </button>
                    )}
                  </div>
                </div>
              </section>
            ))}
        </fieldset>
      </div>

      {/* Analysis popup — spinner while the submit POST runs, then options so
          the user can continue or go back to the MOC list. */}
      {showAnalysisPopup && (
        <div className="moc-analysing__overlay">
          <div
            className="moc-analysing"
            role="dialog"
            aria-modal="true"
            aria-label="Analysing MOC"
          >
            {analysisSubmitted ? (
              <>
                <CheckCircleOutlinedIcon
                  className="moc-report-modal__icon"
                  fontSize="large"
                />
                <h2 className="moc-analysing__title">Request submitted</h2>
                <p className="moc-analysing__desc">
                  Your MOC is processing in the background and can take a few
                  minutes. Wait here to watch each section unlock as it&apos;s
                  ready, or head back to the MOC list and reopen it later.
                </p>
                <div className="moc-analysing__actions">
                  <button
                    type="button"
                    className="moc-btn moc-btn--secondary"
                    onClick={handleGoToMocList}
                  >
                    Back to MOC List
                  </button>
                  <button
                    type="button"
                    className="moc-btn moc-btn--dark"
                    onClick={handleWaitForProgress}
                  >
                    Wait here &amp; watch progress
                  </button>
                </div>
              </>
            ) : (
              <>
                <CircularProgress size={48} />
                <h2 className="moc-analysing__title">Processing your MOC</h2>
                <p className="moc-analysing__desc">
                  We&apos;re submitting your change. This may take a moment —
                  please don&apos;t close this window.
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {analysisError && (
        <div
          className="moc-analysing__overlay"
          onClick={() => setAnalysisError(null)}
        >
          <div
            className="moc-analysing"
            role="dialog"
            aria-modal="true"
            aria-label="Analysis error"
            onClick={(event) => event.stopPropagation()}
          >
            <WarningAmberOutlinedIcon
              className="moc-analysing__error-icon"
              fontSize="large"
            />
            <h2 className="moc-analysing__title">Analysis not ready</h2>
            <p className="moc-analysing__desc">{analysisError}</p>
            <div className="moc-analysing__actions">
              <button
                type="button"
                className="moc-btn moc-btn--secondary"
                onClick={() => setAnalysisError(null)}
              >
                Close
              </button>
              <button
                type="button"
                className="moc-btn moc-btn--dark"
                onClick={() => {
                  setAnalysisError(null);
                  handleAnalyseClick();
                }}
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate MOC — an MOC with the same details already exists */}
      {duplicateMoc && (
        <div
          className="moc-analysing__overlay"
          onClick={() => setDuplicateMoc(null)}
        >
          <div
            className="moc-analysing"
            role="dialog"
            aria-modal="true"
            aria-label="MOC already exists"
            onClick={(event) => event.stopPropagation()}
          >
            <InfoOutlinedIcon
              className="moc-report-modal__icon"
              fontSize="large"
            />
            <h2 className="moc-analysing__title">This MOC already exists</h2>
            <p className="moc-analysing__desc">{duplicateMoc.message}</p>
            <p className="moc-analysing__desc">
              Existing MOC ID: <strong>{duplicateMoc.mocId}</strong>
            </p>
            <div className="moc-analysing__actions">
              <button
                type="button"
                className="moc-btn moc-btn--secondary"
                onClick={() => setDuplicateMoc(null)}
              >
                Close
              </button>
              <button
                type="button"
                className="moc-btn moc-btn--dark"
                onClick={handleViewDuplicateMoc}
              >
                View existing MOC
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Final report ready — download popup */}
      {showReportModal && (
        <div
          className="moc-analysing__overlay"
          onClick={() => setShowReportModal(false)}
        >
          <div
            className="moc-analysing"
            role="dialog"
            aria-modal="true"
            aria-label="Final MOC report ready"
            onClick={(event) => event.stopPropagation()}
          >
            <CheckCircleOutlinedIcon
              className="moc-report-modal__icon"
              fontSize="large"
            />
            <h2 className="moc-analysing__title">Final MOC Report Ready</h2>
            <p className="moc-analysing__desc">
              Your report has been generated. Download it here, or find it later
              on the MOC List page.
            </p>
            <div className="moc-analysing__actions">
              <button
                type="button"
                className="moc-btn moc-btn--secondary"
                onClick={() => setShowReportModal(false)}
              >
                Close
              </button>
              <button
                type="button"
                className="moc-btn moc-btn--dark"
                onClick={handleDownloadReport}
              >
                <FileDownloadOutlinedIcon fontSize="small" />
                <span>Download PDF</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Select Engineering Assets modal */}
      {modalOpen && (
        <div
          className="moc-asset-modal__overlay"
          onClick={() => setModalOpen(false)}
        >
          <div
            className="moc-asset-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Select Engineering Assets"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="moc-asset-modal__header">
              <div>
                <h2 className="moc-asset-modal__title">
                  Select Engineering Assets
                </h2>
                <p className="moc-asset-modal__subtitle">
                  {modalTab === "diagram" ? (
                    <>
                      Select the Engineering Drawing for which the{" "}
                      <span className="moc-asset-modal__highlight">
                        Knowledge Graph
                      </span>{" "}
                      has been generated.
                    </>
                  ) : (
                    <>
                      Select the Engineering Documents whose{" "}
                      <span className="moc-asset-modal__highlight">
                        Extraction
                      </span>{" "}
                      has been{" "}
                      <span className="moc-asset-modal__highlight">
                        completed
                      </span>
                      .
                    </>
                  )}
                </p>
              </div>
              <button
                type="button"
                className="moc-asset-modal__close"
                aria-label="Close"
                onClick={() => setModalOpen(false)}
              >
                <CloseIcon />
              </button>
            </div>

            <div className="moc-asset-modal__tabs">
              <button
                type="button"
                className="moc-asset-modal__tab moc-asset-modal__tab--active"
              >
                {modalTab === "diagram"
                  ? "Engineering Drawing"
                  : "Engineering Documents"}
              </button>
            </div>

            <div className="moc-asset-modal__body">
              <span className="moc-asset-modal__list-label">
                {modalTab === "diagram"
                  ? "List Of Drawings"
                  : "List Of Documents"}
              </span>
              <table className="moc-asset-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Document Name</th>
                    <th>Date</th>
                    <th>Source</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {assetLibrary[modalTab].map((doc) => {
                    const checked = draftSelection[modalTab].includes(doc.id);
                    return (
                      <tr
                        key={doc.id}
                        className={checked ? "moc-asset-table__row--sel" : ""}
                      >
                        <td>
                          <input
                            type={modalTab === "diagram" ? "radio" : "checkbox"}
                            name={
                              modalTab === "diagram"
                                ? "engineering-diagram"
                                : undefined
                            }
                            className="moc-asset-checkbox"
                            checked={checked}
                            onChange={() => toggleDraftAsset(modalTab, doc.id)}
                            aria-label={doc.name}
                          />
                        </td>
                        <td className="moc-asset-table__name">{doc.name}</td>
                        <td className="moc-asset-table__muted">{doc.date}</td>
                        <td className="moc-asset-table__muted">{doc.source}</td>
                        <td className="moc-asset-table__muted">
                          {doc.status || "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="moc-asset-modal__selected">
              <div className="moc-asset-modal__selected-info">
                <span className="moc-asset-modal__selected-caption">
                  SELECTED
                </span>
                <span className="moc-asset-modal__selected-count">
                  {draftDocs.length} Documents Selected
                </span>
              </div>
              <div className="moc-asset-modal__chips">
                {draftDocs.map((doc) => (
                  <span className="moc-asset-chip" key={`chip-${doc.id}`}>
                    <DescriptionOutlinedIcon className="moc-asset-chip__icon" />
                    <span className="moc-asset-chip__label">{doc.name}</span>
                  </span>
                ))}
              </div>
            </div>

            <div className="moc-asset-modal__footer">
              <button
                type="button"
                className="moc-btn moc-btn--secondary"
                onClick={() => setModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="moc-btn moc-btn--dark"
                onClick={handleAddDocuments}
                disabled={isSavingMoc}
              >
                <AddIcon fontSize="small" />
                <span>
                  {isSavingMoc
                    ? "Adding..."
                    : modalTab === "diagram"
                    ? "Add Drawing"
                    : "Add Documents"}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateMoc;
